"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import svgPaths from "@/assets/svg/svg-5s97axwnwx";
import imgHeroImageRight from "@/assets/images/landing/b7cf068f2cd000acb93c2d6dcf2c5d20fcc2d70c.png";
import img1 from "@/assets/images/landing/27a8497d5cb712a39a846f3d49443b8283852dca.png";
import img2 from "@/assets/images/landing/1173f0775d4f6c2c6c7caf725e2ec6d6ed6aca1d.png";
import img3 from "@/assets/images/landing/572a6183892ca2df6e032e2c3c86f59b0f2caa70.png";
import img4 from "@/assets/images/landing/e315784949efca620db678761bac77b9ff2a218a.png";
import img5 from "@/assets/images/landing/fcd7e60494e98ef4a34b3228a1783b7f500b0df6.png";
import imgImage33 from "@/assets/images/landing/316db7e18f2c6469e2b864dd65b286c9ffed230a.png";
import imgImage34 from "@/assets/images/landing/4a88318460322cc5e0461d97af7b5d4a60027fe6.png";
import imgImage35 from "@/assets/images/landing/1fbe4faf3baf8258ccec16cbafa92154aaee3c0a.png";
import imgMainImage from "@/assets/images/landing/d94b23136dedb7aac478a6af278c0b61abb28501.png";
import { img } from "@/assets/svg/svg-etrl8";
import {
	Loader2,
	Database,
	Camera,
	MapPin,
	Map as MapLucideIcon,
	Link2,
	CalendarDays,
	Columns3,
	Download,
} from "lucide-react";
import ScalableContainer from "@/components/ui/ScalableContainer";
import USCoverageMapLazy from "./USCoverageMapLazy";
import { useWindowSize } from "@/hooks/useWindowSize";
import MobileLandingPageContent from "./mobile/MobileLandingPageContent";
import SupportContactInlineForm from "@/components/sections/contact/SupportContactInlineForm";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import { getConfig } from "@/services/enrichment-api";
import SearchBar from "@/components/dashboard/SearchBar";
import type { AddressData } from "@/types/address";
import Script from "next/script";
import SampleSearchResultsPreview from "./SampleSearchResultsPreview";

function PromoTextContainer() {
	return (
		<div
			className="content-stretch flex gap-[0.25rem] items-center relative shrink-0"
			data-name="Promo Text Container"
		>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #1C1B1F)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
			<p className="font-['Inter:Extra_Bold',sans-serif] font-extrabold leading-[1.5rem] not-italic relative shrink-0 text-[#12301b] text-[1rem] text-nowrap tracking-[0.01rem] whitespace-pre">
				Try Free for 14 days
			</p>
		</div>
	);
}

function Search({
	onClick,
	isUpgrading,
}: {
	onClick?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<motion.button
			className="bg-[#a7ff5e] h-[4rem] relative rounded-[4.25rem] shrink-0 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
			data-name="Search"
			data-testid="try-free-transform-section-button"
			whileHover={isUpgrading ? {} : { scale: 1.02 }}
			whileTap={isUpgrading ? {} : { scale: 0.98 }}
			transition={{ type: "spring", stiffness: 400, damping: 17 }}
			type="button"
			style={{ cursor: isUpgrading ? "not-allowed" : "pointer" }}
			onClick={onClick}
			disabled={isUpgrading}
		>
			<div className="box-border content-stretch flex gap-[0.25rem] h-[4rem] items-center justify-end overflow-clip px-[2rem] py-[0.5rem] relative rounded-[inherit]">
				{isUpgrading ? (
					<div className="flex items-center gap-2">
						<Loader2 className="w-5 h-5 text-[#12301b] animate-spin" />
						<p className="font-['Inter:Extra_Bold',sans-serif] font-extrabold not-italic text-[#12301b] text-[1rem] text-nowrap tracking-[0.01rem] whitespace-pre">
							Redirecting...
						</p>
					</div>
				) : (
					<PromoTextContainer />
				)}
			</div>
			<div
				aria-hidden="true"
				className="absolute border border-[#dde3e9] border-solid inset-0 pointer-events-none rounded-[4.25rem] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)]"
			/>
		</motion.button>
	);
}

function FeatureContainer({
	onUpgrade,
	isUpgrading,
}: {
	onUpgrade?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<motion.div
			className="relative content-stretch flex gap-[1.5rem] items-center flex-1"
			data-name="Feature Container"
			initial={{ opacity: 0, y: 30 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
		>
			<motion.div
				className="basis-0 font-['Bricolage_Grotesque:Bold',sans-serif] font-bold grow min-h-px min-w-px relative shrink-0 text-[2rem] text-white tracking-[0.03125rem]"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
				initial={{ opacity: 0, x: -30 }}
				animate={{ opacity: 1, x: 0 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true, amount: 0.1 }}
				transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
			>
				<h1 className="m-0 p-0 flex flex-col gap-0.5 sm:gap-1 leading-[1.28] sm:leading-[1.32]">
					<span className="block whitespace-pre-wrap">
						{`Transform your hours of property `}
					</span>
					<span className="block whitespace-pre-wrap">
						{`research into minutes of actionable decisions. `}
					</span>
				</h1>
			</motion.div>
			<motion.div
				initial={{ opacity: 0, x: 30 }}
				animate={{ opacity: 1, x: 0 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true, amount: 0.1 }}
				transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
				data-testid="hero-try-free-button-container"
			>
				<Search onClick={onUpgrade} isUpgrading={isUpgrading} />
			</motion.div>
		</motion.div>
	);
}

function FeatureSectionsContainer({
	onUpgrade,
	isUpgrading,
}: {
	onUpgrade?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<div className="relative flex flex-col gap-8 w-full px-[7.5rem]">
			<div className="relative flex flex-row flex-wrap gap-6 items-center">
				<FeatureContainer
					onUpgrade={onUpgrade}
					isUpgrading={isUpgrading}
				/>
			</div>
			<div className="relative flex flex-row flex-wrap gap-6 justify-center xl:justify-start">
				<FeatureContainer3 />
				<FeatureContainer5 />
				<FeatureContainer7 />
				<FeatureContainerPhotos />
			</div>
		</div>
	);
}

function HowItWorksSection() {
	return (
		<div className="relative flex flex-col gap-8 w-full px-[7.5rem] pb-2 md:pb-4">
			<motion.div
				id="how-it-works"
				data-testid="page-how-it-works-heading"
				className="relative font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.75rem] text-[2rem] text-white tracking-[0.03125rem] w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
				initial={{ opacity: 0, x: -30 }}
				animate={{ opacity: 1, x: 0 }}
				whileInView={{ opacity: 1, x: 0 }}
				viewport={{ once: true, amount: 0.1 }}
				transition={{ duration: 0.6, ease: "easeOut" }}
			>
				<h2 className="text-inherit font-inherit m-0 p-0">
					How it Works?
				</h2>
			</motion.div>
			<div
				className="relative flex flex-row flex-wrap gap-6 items-start"
				style={{ gap: "5rem" }}
			>
				<FeatureContainer2 />
				<FeatureContainer4 />
				<FeatureContainer6 />

				{/* Separator Line 1 - Between FeatureContainer2 and FeatureContainer4 */}
				<div
					className="absolute top-[4.5rem] w-[calc(33.333%-1.5rem)] h-px pointer-events-none hidden lg:block"
					style={{ left: "5rem" }}
				>
					<svg
						className="block w-full h-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 331 1"
					>
						<path
							d="M0 0.5H331"
							stroke="white"
							strokeDasharray="7 7"
							strokeOpacity="0.3"
							strokeWidth="1"
						/>
					</svg>
				</div>

				{/* Separator Line 2 - Between FeatureContainer4 and FeatureContainer6 */}
				<div className="absolute top-[4.5rem] left-[calc(33.333%+6rem)] w-[calc(33.333%-1.5rem)] h-px pointer-events-none hidden lg:block">
					<svg
						className="block w-full h-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 331 1"
					>
						<path
							d="M0 0.5H331"
							stroke="white"
							strokeDasharray="7 7"
							strokeOpacity="0.3"
							strokeWidth="1"
						/>
					</svg>
				</div>
			</div>
		</div>
	);
}

/** Intro + CTA above Property / Map / Auction showcase */
function DashboardWorkspaceIntro() {
	return (
		<motion.div
			className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full gap-y-4"
			initial={{ opacity: 0, y: 16 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.2 }}
			transition={{ duration: 0.5, ease: "easeOut" }}
		>
			<div className="min-w-0 max-w-3xl">
				<p className="text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-[#A7FF5E]/85 mb-2 m-0">
					Inside the platform
				</p>
				<h2
					className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold text-[1.65rem] sm:text-2xl lg:text-[1.875rem] text-white leading-[1.28] sm:leading-[1.32] tracking-[0.02em] m-0 mb-2"
					style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
				>
					Property, map & auction, one workflow for lien investors
				</h2>
				<p className="text-white/65 text-sm sm:text-[0.9375rem] leading-snug m-0 max-w-2xl">
					Same filters on the grid, map, and auction calendar. Export
					anytime—or order ground-level photos per address.
				</p>
			</div>
			<Link
				href="/imagery"
				className="inline-flex items-center justify-center gap-2 shrink-0 self-start lg:self-auto text-sm font-semibold text-[#0f1b13] bg-[#A7FF5E] hover:bg-[#cbf7a6] rounded-full px-5 py-2.5 transition-colors border border-[#A7FF5E] shadow-[0_0_28px_rgba(167,255,94,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A7FF5E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f1b13]"
			>
				<Camera className="size-4 shrink-0" aria-hidden />
				Order property photos
			</Link>
		</motion.div>
	);
}

const WORKSPACE_CARD_SURFACE =
	"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.1) 0%, transparent 46%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.07) 0%, transparent 52%), rgba(22, 37, 28, 0.75)";

/** Shared pill row for Property / Map / Auction feature lists */
const WORKSPACE_FEATURE_ROW_CLASS =
	"content-stretch flex gap-2.5 items-center rounded-xl border border-white/[0.1] bg-black/30 px-3 py-2.5 sm:px-3.5 sm:py-3 backdrop-blur-md w-full max-w-full shadow-sm shadow-black/20";

function FeatureAndPropertyContainer({
	onUpgrade,
	isUpgrading,
}: {
	onUpgrade?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<div className="relative flex flex-col gap-8 md:gap-10 w-full max-w-[90rem] mx-auto px-6 md:px-10 lg:px-[7.5rem]">
			<DashboardWorkspaceIntro />
			{/*
				Bento redesign:
				- xl+: three equal columns — balanced height, no towering left rail
				- lg–xl: property full width, map | auction below
				- <lg: stack
			*/}
			<div className="rounded-[1.75rem] md:rounded-[2rem] border border-white/[0.07] bg-[#0c1611]/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] p-4 sm:p-5 md:p-6 lg:p-7 xl:p-8">
				<div className="grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3 xl:gap-6 xl:items-stretch">
					<div className="min-h-0 flex w-full h-full lg:col-span-2 xl:col-span-1">
						<PropertyViewCard />
					</div>
					<div className="min-h-0 flex w-full h-full flex-col">
						<MapViewCard />
					</div>
					<div className="min-h-0 flex w-full h-full flex-col">
						<AuctionViewCard />
					</div>
				</div>
			</div>
			<div className="relative flex justify-center w-full">
				<TryFreeButtonHowItWorks
					onClick={onUpgrade}
					isUpgrading={isUpgrading}
				/>
			</div>
		</div>
	);
}

function Group1321314512() {
	return (
		<div className="absolute inset-[27.78%]">
			<div className="absolute bottom-[-3.03%] left-0 right-[-3.03%] top-0">
				<svg
					className="block size-full"
					fill="none"
					preserveAspectRatio="none"
					viewBox="0 0 33 33"
				>
					<g id="Group 1321314512">
						<g filter="url(#filter0_n_0_10297)" id="Icon">
							<path
								d={svgPaths.p1575db80}
								fill="var(--fill-0, #25BC45)"
							/>
						</g>
						<g filter="url(#filter1_n_0_10297)" id="Icon_2">
							<path
								d="M32 32.0001L25.9665 25.9664"
								stroke="var(--stroke-0, #25BC45)"
								strokeLinecap="round"
								strokeWidth="1.9394"
							/>
						</g>
					</g>
					<defs>
						<filter
							colorInterpolationFilters="sRGB"
							filterUnits="userSpaceOnUse"
							height="29.3065"
							id="filter0_n_0_10297"
							width="29.3065"
							x="0"
							y="0"
						>
							<feFlood
								floodOpacity="0"
								result="BackgroundImageFix"
							/>
							<feBlend
								in="SourceGraphic"
								in2="BackgroundImageFix"
								mode="normal"
								result="shape"
							/>
							<feTurbulence
								baseFrequency="4.1249890327453613 4.1249890327453613"
								numOctaves="3"
								result="noise"
								seed="662"
								stitchTiles="stitch"
								type="fractalNoise"
							/>
							<feColorMatrix
								in="noise"
								result="alphaNoise"
								type="luminanceToAlpha"
							/>
							<feComponentTransfer
								in="alphaNoise"
								result="coloredNoise1"
							>
								<feFuncA
									tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
									type="discrete"
								/>
							</feComponentTransfer>
							<feComposite
								in="coloredNoise1"
								in2="shape"
								operator="in"
								result="noise1Clipped"
							/>
							<feFlood
								floodColor="rgba(0, 0, 0, 0.37)"
								result="color1Flood"
							/>
							<feComposite
								in="color1Flood"
								in2="noise1Clipped"
								operator="in"
								result="color1"
							/>
							<feMerge result="effect1_noise_0_10297">
								<feMergeNode in="shape" />
								<feMergeNode in="color1" />
							</feMerge>
						</filter>
						<filter
							colorInterpolationFilters="sRGB"
							filterUnits="userSpaceOnUse"
							height="7.97309"
							id="filter1_n_0_10297"
							width="7.97292"
							x="24.9968"
							y="24.9967"
						>
							<feFlood
								floodOpacity="0"
								result="BackgroundImageFix"
							/>
							<feBlend
								in="SourceGraphic"
								in2="BackgroundImageFix"
								mode="normal"
								result="shape"
							/>
							<feTurbulence
								baseFrequency="4.1249890327453613 4.1249890327453613"
								numOctaves="3"
								result="noise"
								seed="662"
								stitchTiles="stitch"
								type="fractalNoise"
							/>
							<feColorMatrix
								in="noise"
								result="alphaNoise"
								type="luminanceToAlpha"
							/>
							<feComponentTransfer
								in="alphaNoise"
								result="coloredNoise1"
							>
								<feFuncA
									tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
									type="discrete"
								/>
							</feComponentTransfer>
							<feComposite
								in="coloredNoise1"
								in2="shape"
								operator="in"
								result="noise1Clipped"
							/>
							<feFlood
								floodColor="rgba(0, 0, 0, 0.37)"
								result="color1Flood"
							/>
							<feComposite
								in="color1Flood"
								in2="noise1Clipped"
								operator="in"
								result="color1"
							/>
							<feMerge result="effect1_noise_0_10297">
								<feMergeNode in="shape" />
								<feMergeNode in="color1" />
							</feMerge>
						</filter>
					</defs>
				</svg>
			</div>
		</div>
	);
}

function FeatureIconContainer1() {
	return (
		<div
			className="backdrop-blur-md backdrop-filter relative rounded-[3.5rem] shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
		>
			<div className="overflow-clip relative rounded-[inherit] size-[4.5rem]">
				<Group1321314512 />
			</div>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-solid border-[rgba(255,255,255,0.12)] inset-0 pointer-events-none rounded-[3.5rem]"
			/>
			{/* Green hover border */}
			<div
				aria-hidden="true"
				className="absolute border border-solid border-[#A7FF5E] inset-0 pointer-events-none rounded-[3.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
			/>
		</div>
	);
}

function FeatureDescriptionContainer() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.75rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<h3 className="font-['Inter:Black',sans-serif] font-black leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Discover
			</h3>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				Instantly search our comprehensive tax lien property database
				with advanced filters to find opportunities that match your
				investment goals.
			</p>
		</div>
	);
}

function FeatureContainer2() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[21.75rem] items-start overflow-clip pl-0 pr-[2.5rem] py-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[21.5rem] max-w-[24rem] group"
			data-name="Feature Container"
			data-testid="how-it-works-card-discover"
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer1 />
			<FeatureDescriptionContainer />
		</motion.div>
	);
}

/** Shared label row for research cards (Photos uses its own “New” pill hero treatment) */
function FeaturePlatformBadgeRow({
	eyebrow,
	tagline,
}: {
	eyebrow: string;
	tagline: string;
}) {
	return (
		<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
			<span className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-[#A7FF5E]/80">
				{eyebrow}
			</span>
			<span className="text-white/25" aria-hidden>
				·
			</span>
			<span className="text-[0.625rem] font-medium uppercase tracking-[0.1em] text-white/45">
				{tagline}
			</span>
		</div>
	);
}

function FeatureResearchFootnote({ children }: { children: React.ReactNode }) {
	return (
		<p className="text-[0.8125rem] text-white/45 leading-snug m-0 pt-1 border-t border-white/[0.08] mt-2">
			{children}
		</p>
	);
}

function FeatureIconContainer2() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem] rounded-[1.25rem] border border-white/12 bg-gradient-to-br from-[rgba(101,222,158,0.09)] to-[rgba(22,37,28,0.4)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] flex items-center justify-center overflow-hidden"
			data-name="Feature Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 72"
			>
				<g id="Feature Icon Container">
					<g filter="url(#filter1_n_0_10354)" id="villa">
						<path
							d={svgPaths.p2ddd5a00}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="64"
						id="filter1_n_0_10354"
						width="64"
						x="0"
						y="-4.92599e-09"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2 2"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10354">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

export function FeatureDescriptionContainer1() {
	return (
		<div
			className="content-stretch flex flex-col gap-2.5 items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
			data-testid="transform-card-property-insights"
		>
			<FeaturePlatformBadgeRow
				eyebrow="Research"
				tagline="Included with Pro"
			/>
			<h3 className="font-['Inter:Black',sans-serif] font-black leading-[1.85rem] relative shrink-0 text-[1.2rem] text-white break-words">
				Verified Property Insights
			</h3>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.45rem] w-full relative shrink-0 text-[0.9375rem] text-[rgba(255,255,255,0.72)] break-words">
				Parcel, use, valuations, and risk signals in one
				place—everything you need to shortlist liens before you dig
				deeper.
			</p>
			<FeatureResearchFootnote>
				Search any address nationwide from your dashboard.
			</FeatureResearchFootnote>
		</div>
	);
}

function FeatureContainer3() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[1.35rem] min-h-[19.5rem] items-start overflow-visible p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[24rem] max-w-[24rem] group ring-1 ring-[#A7FF5E]/10 shadow-[0_14px_48px_-16px_rgba(0,0,0,0.55)]"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.1) 0%, transparent 46%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.08) 0%, transparent 48%), rgba(22, 37, 28, 0.52)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
			whileHover={{ scale: 1.04, y: -6, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer2 />
			<FeatureDescriptionContainer1 />
			{/* Shining border — subtle at rest, full on hover */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-[0.14] group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.25), inset 0 0 20px rgba(167, 255, 94, 0.08)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function FeatureIconContainer3() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 72"
			>
				<foreignObject height="120" width="120" x="-24" y="-24">
					<div
						style={{
							backdropFilter: "blur(12px)",
							clipPath: "url(#bgblur_0_0_10371_clip_path)",
							height: "100%",
							width: "100%",
						}}
					/>
				</foreignObject>
				<g data-figma-bg-blur-radius="24" id="Feature Icon Container">
					{/* Base border */}
					<rect
						height="71"
						rx="35.5"
						stroke="rgba(255,255,255,0.12)"
						width="71"
						x="0.5"
						y="0.5"
					/>
					{/* Green hover border */}
					<rect
						height="71"
						rx="35.5"
						stroke="#A7FF5E"
						width="71"
						x="0.5"
						y="0.5"
						className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
					/>
					<g filter="url(#filter1_n_0_10371)" id="Icon">
						<path
							d={svgPaths.p11809380}
							fill="var(--fill-0, #25BC45)"
						/>
						<path
							d={svgPaths.pe17fa00}
							fill="var(--fill-0, #25BC45)"
						/>
						<path
							d={svgPaths.p2ca0a280}
							fill="var(--fill-0, #25BC45)"
						/>
						<path
							d={svgPaths.p19cfd900}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
				</g>
				<defs>
					<clipPath
						id="bgblur_0_0_10371_clip_path"
						transform="translate(24 24)"
					>
						<rect
							height="71"
							rx="35.5"
							width="71"
							x="0.5"
							y="0.5"
						/>
					</clipPath>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="36"
						id="filter1_n_0_10371"
						width="36"
						x="18"
						y="18"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="3.5555555820465088 3.5555555820465088"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10371">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
					<linearGradient
						gradientUnits="userSpaceOnUse"
						id="paint0_linear_0_10371"
						x1="36"
						x2="36"
						y1="0"
						y2="72"
					>
						<stop stopColor="white" />
						<stop offset="1" stopColor="white" stopOpacity="0" />
					</linearGradient>
				</defs>
			</svg>
		</div>
	);
}

function FeatureDescriptionContainer2() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.75rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<h3 className="font-['Inter:Bold',sans-serif] font-bold leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Evaluate
			</h3>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				Access detailed financial and legal information—debt amounts,
				case numbers, property values, and owner information—all in one
				easy-to-use dashboard.
			</p>
		</div>
	);
}

function FeatureContainer4() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] items-start overflow-clip pl-0 pr-[2.5rem] py-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[21.5rem] max-w-[21.5rem] group"
			data-name="Feature Container"
			data-testid="how-it-works-card-evaluate"
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer3 />
			<FeatureDescriptionContainer2 />
		</motion.div>
	);
}

function FeatureIconContainer4() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem] rounded-[1.25rem] border border-white/12 bg-gradient-to-br from-[rgba(101,222,158,0.09)] to-[rgba(22,37,28,0.4)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] flex items-center justify-center overflow-hidden"
			data-name="Feature Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 72"
			>
				<g id="Feature Icon Container">
					<g filter="url(#filter1_n_0_10364)" id="groups">
						<path
							d={svgPaths.pa113900}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="35"
						id="filter1_n_0_10364"
						width="64"
						x="0"
						y="18"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2 2"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10364">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
				</defs>
			</svg>
		</div>
	);
}

function FeatureDescriptionContainer3() {
	return (
		<div
			className="content-stretch flex flex-col gap-2.5 items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full max-w-full pr-1"
			data-name="Feature Description Container"
			data-testid="transform-card-owner-information"
		>
			<FeaturePlatformBadgeRow
				eyebrow="Contacts"
				tagline="Verified where available"
			/>
			<p className="font-['Inter:Black',sans-serif] font-black leading-[1.85rem] relative shrink-0 text-[1.2rem] text-white break-words">
				Accurate Owner Information
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.45rem] w-full relative shrink-0 text-[0.9375rem] text-[rgba(255,255,255,0.72)] break-words">
				Match deeds to decision-makers—reach the right owner or entity
				without chasing dead-end leads.
			</p>
			<FeatureResearchFootnote>
				Tied to the same property record you’re already reviewing.
			</FeatureResearchFootnote>
		</div>
	);
}

function FeatureContainer5() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter min-h-[19.5rem] overflow-visible rounded-[1.8125rem] flex-1 min-w-[24rem] max-w-[24rem] group ring-1 ring-[#A7FF5E]/10 shadow-[0_14px_48px_-16px_rgba(0,0,0,0.55)]"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.1) 0%, transparent 46%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.08) 0%, transparent 48%), rgba(22, 37, 28, 0.52)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
			whileHover={{ scale: 1.04, y: -6, transition: { duration: 0.3 } }}
		>
			<div className="box-border p-[2.5rem] h-full overflow-clip flex flex-col gap-[1.35rem]">
				<FeatureIconContainer4 />
				<FeatureDescriptionContainer3 />
			</div>
			{/* Shining border */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-[0.14] group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.25), inset 0 0 20px rgba(167, 255, 94, 0.08)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function FeatureIconContainer5() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem]"
			data-name="Feature Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 72"
			>
				<foreignObject height="120" width="120" x="-24" y="-24">
					<div
						style={{
							backdropFilter: "blur(12px)",
							clipPath: "url(#bgblur_0_0_10357_clip_path)",
							height: "100%",
							width: "100%",
						}}
					/>
				</foreignObject>
				<g data-figma-bg-blur-radius="24" id="Feature Icon Container">
					{/* Base border */}
					<rect
						height="71"
						rx="35.5"
						stroke="rgba(255,255,255,0.12)"
						width="71"
						x="0.5"
						y="0.5"
					/>
					{/* Green hover border */}
					<rect
						height="71"
						rx="35.5"
						stroke="#A7FF5E"
						width="71"
						x="0.5"
						y="0.5"
						className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
					/>
					<g filter="url(#filter1_n_0_10357)" id="Icon">
						<path
							d={svgPaths.p2220fa80}
							fill="var(--fill-0, #25BC45)"
						/>
						<path
							d={svgPaths.p1e4db600}
							fill="var(--fill-0, #25BC45)"
						/>
						<path
							d={svgPaths.p3b88280}
							fill="var(--fill-0, #25BC45)"
						/>
						<path
							d={svgPaths.p78f6f80}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
					<g filter="url(#filter2_n_0_10357)" id="Icon_2">
						<path
							d={svgPaths.p890c80}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
				</g>
				<defs>
					<clipPath
						id="bgblur_0_0_10357_clip_path"
						transform="translate(24 24)"
					>
						<rect
							height="71"
							rx="35.5"
							width="71"
							x="0.5"
							y="0.5"
						/>
					</clipPath>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="34.1999"
						id="filter1_n_0_10357"
						width="35.9999"
						x="18"
						y="19.7998"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="3.5555665493011475 3.5555665493011475"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10357">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="12.6"
						id="filter2_n_0_10357"
						width="11.6469"
						x="28.8001"
						y="18"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="3.5555665493011475 3.5555665493011475"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10357">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
					<linearGradient
						gradientUnits="userSpaceOnUse"
						id="paint0_linear_0_10357"
						x1="36"
						x2="36"
						y1="0"
						y2="72"
					>
						<stop stopColor="white" />
						<stop offset="1" stopColor="white" stopOpacity="0" />
					</linearGradient>
				</defs>
			</svg>
		</div>
	);
}

function FeatureDescriptionContainer4() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.75rem] items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
		>
			<h3 className="font-['Inter:Bold',sans-serif] font-bold leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white whitespace-pre">
				Bid
			</h3>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] min-w-full relative shrink-0 text-[1rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				Make confident investment decisions using verified data, connect
				with property owners, coordinate with legal experts, and secure
				your next tax lien investment.
			</p>
		</div>
	);
}

function FeatureContainer6() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[2rem] h-[21.75rem] items-start overflow-clip pl-0 pr-[2.5rem] py-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[21.5rem] max-w-[21.5rem] group"
			data-name="Feature Container"
			data-testid="how-it-works-card-invest"
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
			whileHover={{ y: -8, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer5 />
			<FeatureDescriptionContainer4 />
		</motion.div>
	);
}

function FeatureIconContainer6() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem] rounded-[1.25rem] border border-white/12 bg-gradient-to-br from-[rgba(101,222,158,0.09)] to-[rgba(22,37,28,0.4)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] flex items-center justify-center overflow-hidden"
			data-name="Feature Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 72 72"
			>
				<g clipPath="url(#clip0_0_10269)" id="Feature Icon Container">
					<g filter="url(#filter1_n_0_10269)" id="home_storage">
						<path
							d={svgPaths.p3b2d29f0}
							fill="var(--fill-0, #25BC45)"
						/>
					</g>
				</g>
				<defs>
					<filter
						colorInterpolationFilters="sRGB"
						filterUnits="userSpaceOnUse"
						height="73.1961"
						id="filter1_n_0_10269"
						width="64"
						x="8"
						y="0"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feBlend
							in="SourceGraphic"
							in2="BackgroundImageFix"
							mode="normal"
							result="shape"
						/>
						<feTurbulence
							baseFrequency="2.9307968616485596 2.9307968616485596"
							numOctaves="3"
							result="noise"
							seed="662"
							stitchTiles="stitch"
							type="fractalNoise"
						/>
						<feColorMatrix
							in="noise"
							result="alphaNoise"
							type="luminanceToAlpha"
						/>
						<feComponentTransfer
							in="alphaNoise"
							result="coloredNoise1"
						>
							<feFuncA
								tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
								type="discrete"
							/>
						</feComponentTransfer>
						<feComposite
							in="coloredNoise1"
							in2="shape"
							operator="in"
							result="noise1Clipped"
						/>
						<feFlood
							floodColor="rgba(0, 0, 0, 0.37)"
							result="color1Flood"
						/>
						<feComposite
							in="color1Flood"
							in2="noise1Clipped"
							operator="in"
							result="color1"
						/>
						<feMerge result="effect1_noise_0_10269">
							<feMergeNode in="shape" />
							<feMergeNode in="color1" />
						</feMerge>
					</filter>
					<clipPath id="clip0_0_10269">
						<rect fill="white" height="72" width="72" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function FeatureDescriptionContainer5() {
	return (
		<div
			className="content-stretch flex flex-col gap-2.5 items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
			data-testid="transform-card-financial-legal"
		>
			<FeaturePlatformBadgeRow
				eyebrow="Diligence"
				tagline="Liens, judgments & more"
			/>
			<div className="font-['Inter:Black',sans-serif] font-black leading-[1.85rem] relative shrink-0 text-[1.2rem] text-white break-words">
				<p className="mb-0">{`Comprehensive Financial &`}</p>
				<p className="mb-0">Legal Data</p>
			</div>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.45rem] min-w-full relative shrink-0 text-[0.9375rem] text-[rgba(255,255,255,0.72)] w-full break-words">
				Debt stacks, filings, and case references surfaced next to the
				asset—so underwriting happens before you wire funds.
			</p>
			<FeatureResearchFootnote>
				Everything references the same property you’re evaluating.
			</FeatureResearchFootnote>
		</div>
	);
}

function FeatureContainer7() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[1.35rem] min-h-[19.5rem] items-start overflow-visible p-[2.5rem] rounded-[1.8125rem] flex-1 min-w-[24rem] max-w-[24rem] group ring-1 ring-[#A7FF5E]/10 shadow-[0_14px_48px_-16px_rgba(0,0,0,0.55)]"
			data-name="Feature Container"
			style={{
				background:
					"radial-gradient(circle at 0% 0%, rgba(101, 222, 158, 0.1) 0%, transparent 46%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.08) 0%, transparent 48%), rgba(22, 37, 28, 0.52)",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
			whileHover={{ scale: 1.04, y: -6, transition: { duration: 0.3 } }}
		>
			<FeatureIconContainer6 />
			<FeatureDescriptionContainer5 />
			{/* Shining border */}
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-[0.14] group-hover:opacity-100 transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 20px rgba(167, 255, 94, 0.25), inset 0 0 20px rgba(167, 255, 94, 0.08)",
				}}
			/>
			{/* Base border */}
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
			/>
		</motion.div>
	);
}

function FeaturePhotosIconContainer() {
	return (
		<div
			className="relative shrink-0 size-[4.5rem] rounded-[1.25rem] border-2 border-[#A7FF5E]/55 bg-gradient-to-br from-[rgba(167,255,94,0.22)] to-[rgba(15,27,19,0.55)] flex items-center justify-center shadow-[0_0_32px_rgba(167,255,94,0.35)]"
			aria-hidden
		>
			<Camera className="size-8 text-[#A7FF5E]" strokeWidth={2} />
		</div>
	);
}

function FeatureDescriptionContainerPhotos() {
	return (
		<div
			className="content-stretch flex flex-col gap-2.5 items-start not-italic relative shrink-0 tracking-[0.03125rem] w-full"
			data-name="Feature Description Container"
			data-testid="transform-card-property-photos"
		>
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-[0.625rem] font-bold uppercase tracking-[0.14em] text-[#0f1b13] bg-[#A7FF5E] px-2.5 py-1 rounded-full">
					New
				</span>
				<span className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-[#A7FF5E]">
					Order à la carte
				</span>
			</div>
			<h3 className="font-['Inter:Black',sans-serif] font-black leading-[1.85rem] relative shrink-0 text-[1.2rem] text-white break-words">
				Ground-Level Property Photos
			</h3>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.45rem] w-full relative shrink-0 text-[0.9375rem] text-[rgba(255,255,255,0.72)] break-words">
				Real exterior imagery for any US address—see the property before
				you bid, not a satellite tile.
			</p>
			<ol className="list-none w-full space-y-1.5 pt-0.5 m-0 p-0">
				<li className="flex gap-2 items-start text-[0.875rem] text-white/90 leading-snug">
					<span className="font-black text-[#A7FF5E] shrink-0 w-5">
						1.
					</span>
					<span>Add the property address</span>
				</li>
				<li className="flex gap-2 items-start text-[0.875rem] text-white/90 leading-snug">
					<span className="font-black text-[#A7FF5E] shrink-0 w-5">
						2.
					</span>
					<span>Pay securely online</span>
				</li>
				<li className="flex gap-2 items-start text-[0.875rem] text-white/90 leading-snug">
					<span className="font-black text-[#A7FF5E] shrink-0 w-5">
						3.
					</span>
					<span>Photos reach you in 24–72 hours</span>
				</li>
			</ol>
			<p className="text-[#A7FF5E] text-sm font-semibold mt-1 mb-0">
				Order photos →
			</p>
		</div>
	);
}

function FeatureContainerPhotos() {
	return (
		<Link
			href="/imagery"
			className="flex-1 min-w-[min(100%,24rem)] max-w-[24rem] shrink-0 rounded-[1.8125rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A7FF5E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f1b13]"
		>
			<motion.div
				className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-[1.35rem] min-h-[20.5rem] h-full items-start overflow-visible p-[2.5rem] rounded-[1.8125rem] flex-1 group ring-2 ring-[#A7FF5E]/45 shadow-[0_0_44px_rgba(167,255,94,0.22),0_18px_56px_-18px_rgba(0,0,0,0.65)]"
				style={{
					background:
						"radial-gradient(circle at 12% 0%, rgba(167, 255, 94, 0.22) 0%, transparent 38%), radial-gradient(circle at 100% 100%, rgba(101, 222, 158, 0.12) 0%, transparent 48%), rgba(22, 37, 28, 0.58)",
				}}
				initial={{ opacity: 0, y: 50 }}
				animate={{ opacity: 1, y: 0 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, margin: "-100px", amount: 0.1 }}
				transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
				whileHover={{
					scale: 1.06,
					y: -10,
					transition: { duration: 0.3 },
				}}
			>
				<FeaturePhotosIconContainer />
				<FeatureDescriptionContainerPhotos />
				<div
					aria-hidden="true"
					className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[1.8125rem] opacity-[0.72] group-hover:opacity-100 transition-opacity duration-300"
					style={{
						boxShadow:
							"0 0 28px rgba(167, 255, 94, 0.45), inset 0 0 22px rgba(167, 255, 94, 0.1)",
					}}
				/>
				<div
					aria-hidden="true"
					className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.8125rem]"
				/>
			</motion.div>
		</Link>
	);
}

// Header component removed - now using shared Header from @/components/layout/Header

function Search1() {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);

	const handleAddressSelect = (address: AddressData) => {
		if (address.latitude && address.longitude) {
			setIsLoading(true);
			const params = new URLSearchParams();
			params.set("lat", address.latitude.toString());
			params.set("lng", address.longitude.toString());
			params.set("address", encodeURIComponent(address.formattedAddress));
			router.push(`/playground?${params.toString()}`);
		}
	};

	return (
		<motion.div
			className="relative w-[72%] max-w-[62rem] min-w-[18.75rem]"
			data-name="Search"
			data-testid="hero-search"
			style={{ minWidth: "43.75rem", zIndex: 1000, overflow: "visible" }}
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
			whileHover={{ scale: 1.01 }}
		>
			<div className="max-w-[62rem] mx-auto overflow-visible">
				<SearchBar
					onAddressSelect={handleAddressSelect}
					isLoading={isLoading}
					fullWidth={true}
					inputClassName="!h-[4.5rem] !text-xl !px-12 !pr-16"
				/>
			</div>
			<SampleSearchResultsPreview
				compact
				className="mt-5 w-full"
				title="Properties in New Castle"
				description="Real properties currently available in New Castle, Delaware."
			/>
		</motion.div>
	);
}

function PromoTextContainer1({ onClick }: { onClick?: () => void }) {
	return (
		<button
			onClick={onClick}
			className="relative content-stretch flex gap-[0.25rem] items-center cursor-pointer bg-transparent border-none p-0"
			data-name="Promo Text Container"
			data-testid="try-free-14days-button"
			type="button"
			style={{ cursor: "pointer" }}
		>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
			<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[0.875rem] not-italic relative shrink-0 text-[#cbf7a6] text-[0.75rem] text-nowrap tracking-[0.0075rem] whitespace-pre">
				<span>{`Try Free for 14 days • `}</span>
				<span className="text-[#dde3e9]">{`No Credit Card Required • Cancel Anytime  • Priority  Support`}</span>
			</p>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
		</button>
	);
}

function HeroContent({
	onUpgrade,
	enrichmentEnabled,
}: {
	onUpgrade?: () => void;
	isUpgrading?: boolean;
	enrichmentEnabled?: boolean;
}) {
	const router = useRouter();
	return (
		<div
			className="absolute left-[14.3%] top-[19.5rem] z-10 w-[71.4%] max-w-[64.25rem] flex flex-col items-center gap-7 pb-12"
			data-name="Hero Content"
			data-testid="hero-section"
		>
			<div className="relative font-['Montserrat:SemiBold',sans-serif] font-semibold leading-[0] text-[0rem] text-center text-white tracking-[0.03125rem] w-full max-w-[64.25rem]">
				<p
					className="font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] mb-0 text-[3rem]"
					style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
					data-testid="hero-heading"
				>{`Smarter Tax Lien Property `}</p>
				<p className="font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] text-[3rem]">
					<span
						style={{
							fontVariationSettings: "'opsz' 14, 'wdth' 100",
						}}
					>{`Research for `}</span>
					<span
						className="text-[#a7ff5e]"
						style={{
							fontVariationSettings: "'opsz' 14, 'wdth' 100",
						}}
					>
						Profitable
					</span>
					<span
						className="text-white"
						style={{
							fontVariationSettings: "'opsz' 14, 'wdth' 100",
						}}
					>{` Investments`}</span>
				</p>
			</div>
			<Search1 />
			<div className="flex flex-wrap items-center justify-center gap-3">
				{enrichmentEnabled && (
					<motion.button
						type="button"
						onClick={() => router.push("/enrich")}
						className="bg-white/10 hover:bg-white/20 border border-[#A7FF5E] text-[#A7FF5E] h-[4rem] px-[2rem] rounded-[4.25rem] font-['Inter:Extra_Bold',sans-serif] font-extrabold text-[1rem] flex items-center justify-center gap-2 transition-colors"
						whileHover={{ scale: 1.02 }}
						whileTap={{ scale: 0.98 }}
					>
						<Database className="w-5 h-5" />
						Enrichment
					</motion.button>
				)}
				<PromoTextContainer1 onClick={onUpgrade} />
			</div>
		</div>
	);
}

function HeroSection({
	onUpgrade,
	isUpgrading,
	enrichmentEnabled,
}: {
	onUpgrade?: () => void;
	isUpgrading?: boolean;
	enrichmentEnabled?: boolean;
}) {
	return (
		<div
			className="relative bg-[#1a2c20] min-h-[76rem] overflow-hidden w-full max-w-[90rem] flex flex-col items-center justify-center"
			data-name="Hero Section"
		>
			<div className="absolute left-0 z-0 w-[32%] max-w-[29.125rem] min-w-[12.5rem] aspect-square top-[-11.5625rem] opacity-50 hero-shadow-bg">
				<div className="absolute inset-[-85.837%]">
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 1266 1266"
					>
						<g filter="url(#filter0_fn_0_10344)" id="Ellipse 852">
							<circle
								cx="633"
								cy="633"
								fill="var(--fill-0, #044B27)"
								r="233"
							/>
						</g>
						<defs>
							<filter
								colorInterpolationFilters="sRGB"
								filterUnits="userSpaceOnUse"
								height="1266"
								id="filter0_fn_0_10344"
								width="1266"
								x="0"
								y="0"
							>
								<feFlood
									floodOpacity="0"
									result="BackgroundImageFix"
								/>
								<feBlend
									in="SourceGraphic"
									in2="BackgroundImageFix"
									mode="normal"
									result="shape"
								/>
								<feGaussianBlur
									result="effect1_foregroundBlur_0_10344"
									stdDeviation="200"
								/>
								<feTurbulence
									baseFrequency="0.40000000596046448 0.40000000596046448"
									numOctaves="3"
									result="noise"
									seed="234"
									stitchTiles="stitch"
									type="fractalNoise"
								/>
								<feColorMatrix
									in="noise"
									result="alphaNoise"
									type="luminanceToAlpha"
								/>
								<feComponentTransfer
									in="alphaNoise"
									result="coloredNoise1"
								>
									<feFuncA
										tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
										type="discrete"
									/>
								</feComponentTransfer>
								<feComposite
									in="coloredNoise1"
									in2="effect1_foregroundBlur_0_10344"
									operator="in"
									result="noise1Clipped"
								/>
								<feFlood
									floodColor="rgba(255, 255, 255, 0.12)"
									result="color1Flood"
								/>
								<feComposite
									in="color1Flood"
									in2="noise1Clipped"
									operator="in"
									result="color1"
								/>
								<feMerge result="effect2_noise_0_10344">
									<feMergeNode in="effect1_foregroundBlur_0_10344" />
									<feMergeNode in="color1" />
								</feMerge>
							</filter>
						</defs>
					</svg>
				</div>
			</div>
			<HeroContent
				onUpgrade={onUpgrade}
				isUpgrading={isUpgrading}
				enrichmentEnabled={enrichmentEnabled}
			/>
			<div className="absolute inset-x-0 bottom-0 z-0 h-[24rem] pointer-events-none">
				{/* LCP Image - rendered immediately without animation wrapper for priority */}
				<div
					className="absolute bottom-0 right-0 h-[18.5625rem] opacity-30 w-[33%] max-w-[29.8125rem] min-w-[12.5rem]"
					data-name="Hero Image Right"
				>
					<div className="relative h-full w-full">
						<Image
							alt="Tax Lien Vest Dashboard Interface showcasing property map and data"
							className="object-[50%_50%] object-cover pointer-events-none"
							src={imgHeroImageRight}
							fill
							style={{ top: "2rem" }}
							sizes="(max-width: 1440px) 33vw, 477px"
							priority
						/>
					</div>
				</div>
				<motion.div
					className="absolute bottom-0 flex h-[18.5625rem] items-center justify-center left-0 w-[33%] max-w-[29.8125rem] min-w-[12.5rem] opacity-30"
					initial={{ opacity: 0, x: -50 }}
					animate={{ opacity: 0.3, x: 0 }}
					transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
				>
					<div className="flex-none rotate-[180deg] scale-y-[-100%] relative h-full w-full">
						<Image
							alt=""
							className="object-[50%_50%] object-cover pointer-events-none"
							src={imgHeroImageRight}
							fill
							style={{ top: "2rem" }}
							sizes="(max-width: 1440px) 33vw, 477px"
						/>
					</div>
				</motion.div>
			</div>
		</div>
	);
}

function Search2() {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);

	const handleAddressSelect = (address: AddressData) => {
		if (address.latitude && address.longitude) {
			setIsLoading(true);
			const params = new URLSearchParams();
			params.set("lat", address.latitude.toString());
			params.set("lng", address.longitude.toString());
			params.set("address", encodeURIComponent(address.formattedAddress));
			router.push(`/playground?${params.toString()}`);
		}
	};

	return (
		<div className="relative w-[72%] max-w-[62rem] min-w-[18.75rem]">
			<motion.div
				className="relative w-full"
				data-name="Search"
				style={{ zIndex: 1000, overflow: "visible" }}
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.6, ease: "easeOut" }}
				whileHover={{ scale: 1.01 }}
			>
				<div className="max-w-[62rem] mx-auto overflow-visible">
					<SearchBar
						onAddressSelect={handleAddressSelect}
						isLoading={isLoading}
						fullWidth={true}
						inputClassName="!h-[4.5rem] !text-xl !px-12 !pr-16"
					/>
				</div>
				<SampleSearchResultsPreview
					compact
					className="mt-5 w-full"
					title="Properties in New Castle"
					description="Real properties currently available in New Castle, Delaware."
				/>
			</motion.div>
		</div>
	);
}

function PromoText() {
	return (
		<div
			className="relative h-[0.875rem] w-[30.6875rem]"
			data-name="Promo Text"
		/>
	);
}

function PromoTextContainer2({ onClick }: { onClick?: () => void }) {
	return (
		<button
			onClick={onClick}
			className="relative content-stretch flex gap-[0.25rem] items-center cursor-pointer bg-transparent border-none p-0"
			data-name="Promo Text Container"
			type="button"
			style={{ cursor: "pointer" }}
		>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
			<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[0.875rem] not-italic relative shrink-0 text-[#cbf7a6] text-[0.75rem] text-nowrap tracking-[0.0075rem] whitespace-pre">
				<span>{`Try Free for 14 days • `}</span>
				<span className="text-[#dde3e9]">{`No Credit Card Required • Cancel Anytime  • Priority  Support`}</span>
			</p>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
		</button>
	);
}

function LogoIcon1() {
	return (
		<div
			className="relative shrink-0 size-[2.375rem]"
			data-name="Logo Icon"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 38 38"
			>
				<g id="Logo Icon">
					<circle
						cx="19"
						cy="19"
						fill="var(--fill-0, #52FD8E)"
						id="Ellipse 842"
						r="19"
					/>
					<path
						clipRule="evenodd"
						d={svgPaths.p20133700}
						fill="var(--fill-0, #1B251F)"
						fillRule="evenodd"
						id="image 3 (Traced)"
					/>
				</g>
			</svg>
		</div>
	);
}

function LogoContainer1() {
	return (
		<div
			className="basis-0 content-stretch flex gap-[0.5rem] grow items-center min-h-px min-w-px relative shrink-0"
			data-name="Logo Container"
		>
			<LogoIcon1 />
			<p className="font-['Montserrat:SemiBold',sans-serif] font-semibold leading-[2rem] relative shrink-0 text-[1.25rem] text-nowrap text-white tracking-[0.03125rem] whitespace-pre">
				Tax Lien Vest
			</p>
		</div>
	);
}

function FooterLinks() {
	return (
		<div
			className="content-stretch flex font-['Inter:Medium',sans-serif] font-medium gap-[2.625rem] items-center leading-[1.25rem] not-italic relative shrink-0 text-[1rem] text-nowrap text-right text-white whitespace-pre"
			data-name="Footer Links"
		>
			<Link
				href="/about"
				className="relative shrink-0 hover:text-brand-green transition-colors"
				data-testid="footer-about-link"
			>
				About
			</Link>
			<Link
				href="/privacy-policy"
				className="relative shrink-0 hover:text-brand-green transition-colors"
				data-testid="footer-privacy-policy-link"
			>
				Privacy Policy
			</Link>
			<Link
				href="/terms-of-service"
				className="relative shrink-0 hover:text-brand-green transition-colors"
				data-testid="footer-terms-conditions-link"
			>
				Terms & Conditions
			</Link>
			<Link
				href="/contact-us"
				className="relative shrink-0 hover:text-brand-green transition-colors"
				data-testid="footer-contact-link"
			>
				Contact
			</Link>
		</div>
	);
}

function Content() {
	return (
		<div
			className="basis-0 content-stretch flex gap-[2rem] grow items-center min-h-px min-w-px relative shrink-0"
			data-name="Content"
		>
			<LogoContainer1 />
			<FooterLinks />
		</div>
	);
}

function Container() {
	return (
		<div
			className="content-stretch flex gap-[3rem] items-start relative shrink-0 w-full"
			data-name="Container"
		>
			<Content />
		</div>
	);
}

function Group() {
	return (
		<svg
			className="block size-full"
			fill="none"
			preserveAspectRatio="none"
			viewBox="0 0 24 24"
		>
			<g id="Group">
				<path
					d={svgPaths.p2ccee40}
					fill="var(--fill-0, #8C99A7)"
					id="Vector"
				/>
			</g>
		</svg>
	);
}

function SocialIcons() {
	return (
		<div
			className="content-stretch flex gap-[1.5rem] items-center relative shrink-0"
			data-name="Social icons"
		>
			<div
				className="overflow-clip relative shrink-0 size-[1.5rem]"
				data-name="Social icon"
			>
				<div
					className="absolute bottom-[9.38%] left-0 right-0 top-[9.37%]"
					data-name="Vector"
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 24 20"
					>
						<path
							d={svgPaths.p26cc18f0}
							fill="var(--fill-0, #8C99A7)"
							id="Vector"
						/>
					</svg>
				</div>
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.5rem]"
				data-name="Social icon"
			>
				<Group />
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.5rem]"
				data-name="Social icon"
			>
				<div
					className="absolute bottom-[0.61%] left-0 right-0 top-0"
					data-name="Vector"
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 24 24"
					>
						<path
							d={svgPaths.p2334f790}
							fill="var(--fill-0, #8C99A7)"
							id="Vector"
						/>
					</svg>
				</div>
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.5rem]"
				data-name="Social icon"
			>
				<div
					className="absolute bottom-[2.31%] left-0 right-0 top-0"
					data-name="Icon"
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 24 24"
					>
						<path
							clipRule="evenodd"
							d={svgPaths.pb2a9780}
							fill="var(--fill-0, #8C99A7)"
							fillRule="evenodd"
							id="Icon"
						/>
					</svg>
				</div>
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.5rem]"
				data-name="Social icon"
			>
				<div
					className="absolute bottom-0 left-[15.25%] right-[15.23%] top-0"
					data-name="Vector"
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 17 24"
					>
						<path
							d={svgPaths.p3ae3b680}
							fill="var(--fill-0, #8C99A7)"
							id="Vector"
						/>
					</svg>
				</div>
			</div>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Social icon"
			>
				<svg
					className="block size-full"
					fill="none"
					preserveAspectRatio="none"
					viewBox="0 0 24 24"
				>
					<path
						clipRule="evenodd"
						d={svgPaths.p27316c80}
						fill="var(--fill-0, #8C99A7)"
						fillRule="evenodd"
						id="Vector"
					/>
				</svg>
			</div>
		</div>
	);
}

function Content2() {
	return (
		<div
			className="box-border content-stretch flex items-center justify-between pb-0 pt-[2rem] px-0 relative shrink-0 w-full"
			data-name="Content"
		>
			<div
				aria-hidden="true"
				className="absolute border-[#417714] border-[0.0625rem_0rem_0rem] border-solid inset-0 pointer-events-none"
			/>
			<SocialIcons />
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] not-italic relative shrink-0 text-[#dde3e9] text-[1rem] text-nowrap whitespace-pre">
				© 2025 Lein-Vest. All rights reserved.
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] not-italic relative shrink-0 text-[#dde3e9] text-[0rem] text-[1rem] text-nowrap whitespace-pre flex items-center gap-1">
				<Link
					href="/terms-of-service"
					className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline text-[#dde3e9] hover:text-brand-green transition-colors"
					data-testid="footer-terms-conditions-link-bottom"
				>
					Terms and Condition
				</Link>
				<span>{` and `}</span>
				<Link
					href="/privacy-policy"
					className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid underline text-[#dde3e9] hover:text-brand-green transition-colors"
					data-testid="footer-privacy-policy-link-bottom"
				>
					Privacy policy
				</Link>
			</p>
		</div>
	);
}

function Container1() {
	return (
		<div
			className="content-stretch flex flex-col gap-[2rem] items-start relative shrink-0 w-full"
			data-name="Container"
		>
			<Content2 />
		</div>
	);
}

function Footer() {
	return (
		<div
			className="box-border content-stretch flex flex-col gap-[2.5rem] items-start left-0 px-[7.5rem] py-0 top-[29.25rem] w-full max-w-[90rem]"
			data-name="Footer"
			style={{ minHeight: "15rem" }}
		>
			<Container />
			<Container1 />
		</div>
	);
}

function PromoSection({ onUpgrade }: { onUpgrade?: () => void }) {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter rounded-[1.8125rem] w-full max-w-[90rem] mx-auto flex flex-col items-center gap-10 py-8 md:py-12"
			style={{ overflow: "visible" }}
			data-name="Promo Section"
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<motion.div
				className="relative font-['Bricolage_Grotesque:SemiBold',sans-serif] font-semibold leading-[0] text-[3rem] text-center text-white tracking-[0.03125rem] w-full"
				style={{
					fontVariationSettings: "'opsz' 14, 'wdth' 100",
				}}
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.1 }}
				transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
			>
				<p className="leading-[3.5rem] mb-0">{`See Inside the Platform – `}</p>
				<p className="leading-[3.5rem]">
					<span>{`Access `}</span>
					<span className="text-[#a7ff5e]">
						Professional Property
					</span>
					<span>{` Intelligence Today`}</span>
				</p>
			</motion.div>
			<Search2 />
			<PromoText />
			<PromoTextContainer2 onClick={onUpgrade} />
			<Footer />
		</motion.div>
	);
}

function ContactUsHeader() {
	return (
		<div
			className="content-stretch flex flex-col gap-[1.5rem] items-start relative shrink-0 w-full mb-10"
			data-testid="page-resources-heading"
		>
			<div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-sm border border-white/10">
				<svg
					className="w-8 h-8 text-[#A7FF5E]"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
					/>
				</svg>
			</div>
			<div
				className="flex flex-col font-['Bricolage_Grotesque:96pt_ExtraBold',sans-serif] font-extrabold justify-center leading-[0] min-w-full relative shrink-0 text-[2.75rem] md:text-[3.5rem] text-white w-[min-content]"
				style={{ fontVariationSettings: "'opsz' 96, 'wdth' 100" }}
			>
				<p className="leading-[4rem]">Contact Us</p>
			</div>
		</div>
	);
}

function ContactEmailIcon() {
	return (
		<div className="w-16 h-16 bg-[#A7FF5E] rounded-full flex items-center justify-center shrink-0 shadow-lg group-hover:scale-110 transition-transform">
			<svg
				className="w-8 h-8 text-[#16251c]"
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
				/>
			</svg>
		</div>
	);
}

function ContactEmailText() {
	return (
		<div className="content-stretch flex flex-col items-start justify-center leading-[0] not-italic relative shrink-0 text-nowrap flex-1">
			<div className="flex flex-col font-['Inter:Regular',sans-serif] font-normal justify-center relative shrink-0 text-[#edffde] text-[0.875rem] mb-2 uppercase tracking-wide">
				<p className="leading-[1.25rem] text-nowrap whitespace-pre">
					Email us
				</p>
			</div>
			<a
				href="mailto:support@taxlienvest.com"
				className="flex flex-col font-['Inter:Medium',sans-serif] font-bold justify-center relative shrink-0 text-[1.25rem] text-white hover:text-[#A7FF5E] transition-colors"
			>
				<p className="leading-[2rem] text-nowrap whitespace-pre">
					support@taxlienvest.com
				</p>
			</a>
		</div>
	);
}

function ContactEmailItem() {
	return (
		<div className="content-stretch flex gap-5 items-start relative shrink-0 group">
			<ContactEmailIcon />
			<ContactEmailText />
		</div>
	);
}

function ContactPhoneIcon() {
	return (
		<div className="w-16 h-16 bg-[#A7FF5E] rounded-full flex items-center justify-center shrink-0 shadow-lg group-hover:scale-110 transition-transform">
			<svg
				className="w-8 h-8 text-[#16251c]"
				fill="none"
				viewBox="0 0 24 24"
				stroke="currentColor"
			>
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
				/>
			</svg>
		</div>
	);
}

function ContactPhoneText() {
	return (
		<div className="content-stretch flex flex-col items-start justify-center leading-[0] not-italic relative shrink-0 text-nowrap flex-1">
			<div className="flex flex-col font-['Inter:Regular',sans-serif] font-normal justify-center relative shrink-0 text-[#edffde] text-[0.875rem] mb-2 uppercase tracking-wide">
				<p className="leading-[1.25rem] text-nowrap whitespace-pre">
					Call us
				</p>
			</div>
			<a
				href="tel:+13214857963"
				className="flex flex-col font-['Inter:Medium',sans-serif] font-bold justify-center relative shrink-0 text-[1.25rem] text-white hover:text-[#A7FF5E] transition-colors"
			>
				<p className="leading-[2rem] text-nowrap whitespace-pre">
					+1 (321) 485-7963
				</p>
			</a>
		</div>
	);
}

function ContactPhoneItem() {
	return (
		<div className="content-stretch flex gap-5 items-start relative shrink-0 group">
			<ContactPhoneIcon />
			<ContactPhoneText />
		</div>
	);
}

function ContactInfoContainer() {
	return (
		<div className="relative content-stretch flex flex-col gap-8 items-start flex-1 min-w-[34.625rem] bg-[#16251c] rounded-3xl p-8 md:p-12 text-white shadow-2xl">
			<ContactUsHeader />
			<div className="space-y-8 w-full">
				<ContactEmailItem />
				<ContactPhoneItem />
			</div>
			<div className="mt-12 pt-8 border-t border-white/10 w-full">
				<p className="text-[#8c99a7] text-sm leading-relaxed">
					Our support team typically responds within 24 hours during
					business days.
				</p>
			</div>
		</div>
	);
}

function FormFields() {
	return (
		<>
			<Script
				src="https://challenges.cloudflare.com/turnstile/v0/api.js"
				strategy="afterInteractive"
			/>
			<SupportContactInlineForm variant="landing" idPrefix="landing" />
		</>
	);
}

function ContactFormCard() {
	return (
		<div className="bg-white rounded-3xl border border-gray-200 shadow-2xl p-8 md:p-12 flex-1 min-w-0 max-w-[32rem] mx-auto">
			<FormFields />
		</div>
	);
}

function ContactSection() {
	return (
		<div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start w-full max-w-6xl mx-auto">
			<ContactInfoContainer />
			<ContactFormCard />
		</div>
	);
}

function PromoSection1() {
	return (
		<motion.div
			id="contact"
			className="relative bg-[#16251c] overflow-hidden w-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12 md:py-16"
			data-name="Promo Section"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			whileInView={{ opacity: 1 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<ContactSection />
		</motion.div>
	);
}

function InfoIconContainer1() {
	return (
		<div
			className="relative shrink-0 size-[4rem]"
			data-name="Info Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 64 64"
			>
				<foreignObject height="112" width="112" x="-24" y="-24">
					<div
						style={{
							backdropFilter: "blur(12px)",
							clipPath: "url(#bgblur_0_0_10191_clip_path)",
							height: "100%",
							width: "100%",
						}}
					/>
				</foreignObject>
				<g data-figma-bg-blur-radius="24" id="Info Icon Container">
					<path
						d={svgPaths.p30f5690}
						fill="var(--fill-0, black)"
						fillOpacity="0.05"
					/>
					<path
						d={svgPaths.p30f5690}
						fill="var(--fill-1, #65DE9E)"
						fillOpacity="0.12"
					/>
					<path
						d={svgPaths.pe9f6900}
						fill="var(--fill-0, #A7FF5E)"
						id="villa"
					/>
				</g>
				<defs>
					<clipPath
						id="bgblur_0_0_10191_clip_path"
						transform="translate(24 24)"
					>
						<path d={svgPaths.p30f5690} />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function InfoSectionHeader() {
	return (
		<div
			className="content-stretch flex flex-col gap-6 md:gap-7 items-start relative shrink-0 w-full"
			data-testid="page-payment-heading"
		>
			<InfoIconContainer1 />
			<div
				className="flex flex-col font-['Bricolage_Grotesque:96pt_ExtraBold',sans-serif] font-extrabold justify-center leading-[0] min-w-full relative shrink-0 text-[0rem] text-white w-[min-content]"
				style={{ fontVariationSettings: "'opsz' 96, 'wdth' 100" }}
			>
				<p className="leading-[4.5rem] text-[3.25rem]">
					<span>{`Tax Lien Vest `}</span>
					<span className="text-[#a7ff5e]">PRO</span>
				</p>
			</div>
			<div className="flex flex-col font-['Inter:Regular',sans-serif] font-normal justify-center leading-[0] min-w-full not-italic relative shrink-0 text-[2.5rem] text-[rgba(255,255,255,0.7)] w-[min-content]">
				<p className="leading-[3.5rem]">{`Join Thousands of Smart Investors & Upgrade Your Property Research`}</p>
			</div>
		</div>
	);
}

function InfoSectionContainer() {
	return (
		<div className="relative content-stretch flex flex-col gap-10 md:gap-12 items-start flex-1 min-w-0 lg:min-w-[34.625rem]">
			<InfoSectionHeader />
			<div className="relative content-stretch flex gap-4 md:gap-5 items-center pt-1">
				<div
					className="content-stretch flex gap-[0.5rem] items-start relative shrink-0"
					data-name="Avatar group"
				>
					<Avatars />
				</div>
				<Reviews />
			</div>
		</div>
	);
}

function PaymentOneTimeLabel() {
	return (
		<div className="content-stretch flex gap-[0.625rem] items-center justify-center relative shrink-0">
			<p className="font-['Montserrat:SemiBold',sans-serif] font-semibold leading-[normal] relative shrink-0 text-[1.4375rem] text-center text-nowrap text-white tracking-[0.03125rem] whitespace-pre">
				One-time Payment
			</p>
		</div>
	);
}

function Group1321314511() {
	return (
		<div className="grid-cols-[max-content] grid-rows-[max-content] inline-grid leading-[0] place-items-start relative shrink-0">
			<p className="[grid-area:1_/_1] font-['Montserrat:Medium',sans-serif] font-medium leading-[normal] ml-[4.015375rem] mt-0 relative text-[#8c99a7] text-[2.5rem] text-center text-nowrap tracking-[0.03125rem] translate-x-[-50%] whitespace-pre">
				$299
			</p>
			<div
				className="[grid-area:1_/_1] flex h-[calc(1px*((var(--transform-inner-width)*0.258819043636322)+(var(--transform-inner-height)*0.9659258127212524)))] items-center justify-center ml-0 mt-[0.620313rem] relative w-[calc(1px*((var(--transform-inner-height)*0.258819043636322)+(var(--transform-inner-width)*0.9659258127212524)))]"
				style={
					{
						"--transform-inner-width": "131.984375",
						"--transform-inner-height": "0",
					} as React.CSSProperties
				}
			>
				<div className="flex-none rotate-[345deg]">
					<div className="h-0 relative w-[8.25rem]">
						<div className="absolute bottom-0 left-0 right-0 top-[-0.25rem]">
							<svg
								className="block size-full"
								fill="none"
								preserveAspectRatio="none"
								viewBox="0 0 132 4"
							>
								<line
									id="Line 28"
									stroke="var(--stroke-0, #FC7584)"
									strokeWidth="4"
									x2="132"
									y1="2"
									y2="2"
								/>
							</svg>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function PaymentPriceContainer() {
	return (
		<div className="content-stretch flex gap-[0.625rem] items-center justify-center relative shrink-0">
			<Group1321314511 />
		</div>
	);
}

function PaymentPriceDisplay() {
	return (
		<div className="content-stretch flex gap-[1.125rem] items-end relative shrink-0">
			<PaymentPriceContainer />
			<p className="font-['Montserrat:Bold',sans-serif] font-bold leading-[normal] relative shrink-0 text-[#cbf7a6] text-[5rem] text-center text-nowrap whitespace-pre">
				$49
			</p>
		</div>
	);
}

function PaymentPricingHeader() {
	return (
		<div className="content-stretch flex flex-col gap-4 md:gap-5 items-start relative shrink-0 w-full">
			<PaymentOneTimeLabel />
			<PaymentPriceDisplay />
		</div>
	);
}

function PropertiesInfoContainer() {
	return (
		<div
			className="content-stretch flex gap-[0.4375rem] items-center relative shrink-0 w-full"
			data-name="Properties info container"
		>
			<div
				className="opacity-80 overflow-clip relative shrink-0 size-[1rem]"
				data-name="building-05"
			>
				<div className="absolute inset-[12.5%_8.33%]" data-name="Icon">
					<div
						className="absolute inset-[-5.56%_-5%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 15 14"
						>
							<path
								d={svgPaths.p21490280}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.33333"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.125rem] not-italic opacity-80 relative shrink-0 text-[1rem] text-nowrap text-white tracking-[0.02rem] whitespace-pre">
				Authentic Parcel data
			</p>
		</div>
	);
}

function ParcelInfoContainer() {
	return (
		<div
			className="content-stretch flex gap-[0.4375rem] items-center relative shrink-0 w-full"
			data-name="Parcel info container"
		>
			<div
				className="opacity-80 overflow-clip relative shrink-0 size-[1rem]"
				data-name="database-01"
			>
				<div className="absolute inset-[8.33%_12.5%]" data-name="Icon">
					<div
						className="absolute inset-[-5%_-5.56%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 14 15"
						>
							<path
								d={svgPaths.p370450e0}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.33333"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.125rem] not-italic opacity-80 relative shrink-0 text-[1rem] text-nowrap text-white tracking-[0.02rem] whitespace-pre">
				Trusted Data Sources
			</p>
		</div>
	);
}

function PropertiesInfoContainer1() {
	return (
		<button
			className="content-stretch flex gap-[0.4375rem] items-center relative shrink-0 w-full cursor-pointer bg-transparent border-none p-0"
			data-name="Properties info container"
			type="button"
			style={{ cursor: "pointer" }}
		>
			<div
				className="opacity-80 overflow-clip relative shrink-0 size-[1rem]"
				data-name="currency-dollar"
			>
				<div
					className="absolute bottom-[8.33%] left-1/4 right-1/4 top-[8.33%]"
					data-name="Icon"
				>
					<div
						className="absolute inset-[-5%_-8.33%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 10 15"
						>
							<path
								d={svgPaths.p9364f00}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.33333"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.125rem] not-italic opacity-80 relative shrink-0 text-[1rem] text-nowrap text-white tracking-[0.02rem] whitespace-pre">
				Pricing Data
			</p>
		</button>
	);
}

function ParcelInfoContainer1() {
	return (
		<div
			className="content-stretch flex gap-[0.4375rem] items-center relative shrink-0 w-full"
			data-name="Parcel info container"
		>
			<div
				className="opacity-80 overflow-clip relative shrink-0 size-[1rem]"
				data-name="lightbulb-02"
			>
				<div className="absolute inset-[8.333%]" data-name="Icon">
					<div
						className="absolute inset-[-5%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 15 15"
						>
							<path
								d={svgPaths.p8eb0d00}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.33333"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.125rem] not-italic opacity-80 relative shrink-0 text-[1rem] text-nowrap text-white tracking-[0.02rem] whitespace-pre">
				Fast Property Insights
			</p>
		</div>
	);
}

function ParcelInfoContainer2() {
	return (
		<div
			className="content-stretch flex gap-[0.4375rem] items-center relative shrink-0 w-full"
			data-name="Parcel info container"
		>
			<div
				className="opacity-80 overflow-clip relative shrink-0 size-[1rem]"
				data-name="download-01"
			>
				<div className="absolute inset-[12.5%]" data-name="Icon">
					<div
						className="absolute inset-[-5.56%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 14 14"
						>
							<path
								d={svgPaths.p147bc500}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.33333"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.125rem] not-italic opacity-80 relative shrink-0 text-[1rem] text-nowrap text-white tracking-[0.02rem] whitespace-pre">
				Export Data to google maps
			</p>
		</div>
	);
}

function InfoContainer() {
	return (
		<div
			className="mb-[-0.0625rem] relative rounded-tl-[1rem] rounded-tr-[1rem] shrink-0 w-full"
			data-name="Info container"
		>
			<div className="flex flex-col items-center justify-center size-full">
				<div className="box-border content-stretch flex flex-col gap-[1.25rem] items-center justify-center p-[1.25rem] relative w-full">
					<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[1.125rem] not-italic opacity-80 relative shrink-0 text-[0.75rem] text-white tracking-[0.0075rem] uppercase w-full">
						With Pro yOu Unlock
					</p>
					<PropertiesInfoContainer />
					<ParcelInfoContainer />
					<PropertiesInfoContainer1 />
					<ParcelInfoContainer1 />
					<ParcelInfoContainer2 />
				</div>
			</div>
		</div>
	);
}

function PaymentFeatureCheckmark() {
	return (
		<div className="content-stretch flex gap-[0.75rem] items-center relative shrink-0 w-full">
			<div className="relative shrink-0 size-[0.5rem]">
				<svg
					className="block size-full"
					fill="none"
					preserveAspectRatio="none"
					viewBox="0 0 8 8"
				>
					<circle
						cx="4"
						cy="4"
						fill="var(--fill-0, #A7FF5E)"
						id="Ellipse 853"
						opacity="0.8"
						r="4"
					/>
				</svg>
			</div>
			<p className="font-['Inter:Bold_Italic',sans-serif] font-bold italic leading-[1.125rem] opacity-80 relative shrink-0 text-[0.75rem] text-nowrap text-white tracking-[0.0075rem] uppercase whitespace-pre">
				LIVE Right NoW
			</p>
		</div>
	);
}

function PropertiesInfoContainer2() {
	return (
		<div
			className="content-stretch flex gap-[0.5rem] items-center relative shrink-0 w-full"
			data-name="Properties info container"
		>
			<div
				className="opacity-80 overflow-clip relative shrink-0 size-[1rem]"
				data-name="building-05"
			>
				<div className="absolute inset-[12.5%_8.33%]" data-name="Icon">
					<div
						className="absolute inset-[-5.56%_-5%]"
						style={
							{
								"--stroke-0": "rgba(203, 247, 166, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 15 14"
						>
							<path
								d={svgPaths.p21490280}
								id="Icon"
								stroke="var(--stroke-0, #CBF7A6)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.33333"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="basis-0 font-['Inter:Regular',sans-serif] font-normal grow leading-[1.5rem] min-h-px min-w-px not-italic opacity-80 relative shrink-0 text-[1rem] text-white tracking-[0.02rem]">
				Owner information of more than 50,000 properties properties
				ready to export
			</p>
		</div>
	);
}

function InfoContainer1() {
	return (
		<div
			className="mb-[-0.0625rem] relative rounded-bl-[1rem] rounded-br-[1rem] shrink-0 w-full"
			data-name="Info container"
		>
			<div
				aria-hidden="true"
				className="absolute border-[0.0625rem_0rem_0rem] border-[rgba(255,255,255,0.1)] border-solid inset-0 pointer-events-none rounded-bl-[1rem] rounded-br-[1rem]"
			/>
			<div className="size-full">
				<div className="box-border content-stretch flex flex-col gap-[0.5rem] items-start p-[1.25rem] relative w-full">
					<PaymentFeatureCheckmark />
					<PropertiesInfoContainer2 />
				</div>
			</div>
		</div>
	);
}

function PaymentFeaturesList() {
	return (
		<div className="bg-[rgba(255,255,255,0.05)] box-border content-stretch flex flex-col h-[22.5625rem] items-start pb-px pt-0 px-0 relative rounded-[1rem] shrink-0 w-full">
			<InfoContainer />
			<InfoContainer1 />
		</div>
	);
}

function PaymentSectionCard({
	onClick,
	isUpgrading,
}: {
	onClick?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<div className="relative backdrop-blur-md backdrop-filter box-border content-stretch flex flex-col gap-7 items-start overflow-clip p-[2.5rem] md:p-[2.75rem] rounded-[1.8125rem] flex-1 min-w-0 w-full">
			<PaymentPricingHeader />
			<PaymentFeaturesList />
			<motion.button
				className="bg-[#a7ff5e] h-[3.5rem] relative rounded-[1.75rem] shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] shrink-0 w-full cursor-pointer border-none p-0 disabled:opacity-70 disabled:cursor-not-allowed"
				data-name="Button/Primary"
				data-testid="try-free-pro-button"
				type="button"
				style={{ cursor: isUpgrading ? "not-allowed" : "pointer" }}
				initial={{ opacity: 0, scale: 0.8, y: 20 }}
				whileInView={{ opacity: 1, scale: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{
					type: "spring",
					stiffness: 400,
					damping: 17,
					duration: 0.6,
				}}
				whileHover={isUpgrading ? {} : { scale: 1.02 }}
				whileTap={isUpgrading ? {} : { scale: 0.98 }}
				onClick={onClick}
				disabled={isUpgrading}
			>
				<div className="flex flex-row items-center justify-center overflow-clip rounded-[inherit] size-full">
					<div className="box-border content-stretch flex gap-[0.5rem] h-[3.5rem] items-center justify-center px-[1.5rem] py-[1rem] relative w-full">
						{isUpgrading ? (
							<>
								<Loader2 className="w-5 h-5 text-[#12301b] animate-spin" />
								<p className="font-['Work_Sans:SemiBold',sans-serif] font-semibold leading-[1.75rem] relative shrink-0 text-[#12301b] text-[1.125rem] text-center text-nowrap whitespace-pre">
									Redirecting...
								</p>
							</>
						) : (
							<>
								<div
									className="relative shrink-0 size-[1.5rem]"
									data-name="star_shine"
								>
									<div
										className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
										data-name="star_shine"
										style={{ maskImage: `url('${img}')` }}
									>
										<svg
											className="block size-full"
											fill="none"
											preserveAspectRatio="none"
											viewBox="0 0 21 18"
										>
											<path
												d={svgPaths.p221c8200}
												fill="var(--fill-0, #12301B)"
												id="star_shine"
											/>
										</svg>
									</div>
								</div>
								<p className="font-['Work_Sans:SemiBold',sans-serif] font-semibold leading-[1.75rem] relative shrink-0 text-[#12301b] text-[1.125rem] text-center text-nowrap whitespace-pre">
									Try Free for 14 days
								</p>
							</>
						)}
					</div>
				</div>
			</motion.button>
		</div>
	);
}

function Avatars() {
	return (
		<div
			className="box-border content-stretch flex items-start pl-0 pr-[0.75rem] py-0 relative shrink-0"
			data-name="Avatars"
		>
			<div
				className="box-border content-stretch flex flex-col items-center justify-center mr-[-0.75rem] relative rounded-[12.5rem] shrink-0 size-[2.5rem]"
				data-name="Avatar"
			>
				<div
					aria-hidden="true"
					className="absolute inset-0 pointer-events-none rounded-[12.5rem]"
				>
					<div className="absolute bg-[#cfcbdc] inset-0 rounded-[12.5rem]" />
					<Image
						alt=""
						className="absolute max-w-none object-[50%_50%] object-cover rounded-[12.5rem] size-full"
						src={img1}
						fill
					/>
				</div>
				<div
					aria-hidden="true"
					className="absolute border-[0.09375rem] border-solid border-white inset-[-0.09375rem] pointer-events-none rounded-[12.59375rem]"
				/>
				<div
					className="basis-0 grow min-h-px min-w-px opacity-[0.08] relative rounded-[12.5rem] shrink-0 w-full"
					data-name="_Avatar contrast inner border"
				>
					<div
						aria-hidden="true"
						className="absolute border-[#101828] border-[0.046875rem] border-solid inset-0 pointer-events-none rounded-[12.5rem]"
					/>
				</div>
			</div>
			<div
				className="box-border content-stretch flex flex-col items-center justify-center mr-[-0.75rem] relative rounded-[12.5rem] shrink-0 size-[2.5rem]"
				data-name="Avatar"
			>
				<div
					aria-hidden="true"
					className="absolute inset-0 pointer-events-none rounded-[12.5rem]"
				>
					<div className="absolute bg-[#d6cfb7] inset-0 rounded-[12.5rem]" />
					<Image
						alt=""
						className="absolute max-w-none object-[50%_50%] object-cover rounded-[12.5rem] size-full"
						src={img2}
						fill
					/>
				</div>
				<div
					aria-hidden="true"
					className="absolute border-[0.09375rem] border-solid border-white inset-[-0.09375rem] pointer-events-none rounded-[12.59375rem]"
				/>
				<div
					className="basis-0 grow min-h-px min-w-px opacity-[0.08] relative rounded-[12.5rem] shrink-0 w-full"
					data-name="_Avatar contrast inner border"
				>
					<div
						aria-hidden="true"
						className="absolute border-[#101828] border-[0.046875rem] border-solid inset-0 pointer-events-none rounded-[12.5rem]"
					/>
				</div>
			</div>
			<div
				className="box-border content-stretch flex flex-col items-center justify-center mr-[-0.75rem] relative rounded-[12.5rem] shrink-0 size-[2.5rem]"
				data-name="Avatar"
			>
				<div
					aria-hidden="true"
					className="absolute inset-0 pointer-events-none rounded-[12.5rem]"
				>
					<div className="absolute bg-[#d7e3e8] inset-0 rounded-[12.5rem]" />
					<Image
						alt=""
						className="absolute max-w-none object-[50%_50%] object-cover rounded-[12.5rem] size-full"
						src={img3}
						fill
					/>
				</div>
				<div
					aria-hidden="true"
					className="absolute border-[0.09375rem] border-solid border-white inset-[-0.09375rem] pointer-events-none rounded-[12.59375rem]"
				/>
				<div
					className="basis-0 grow min-h-px min-w-px opacity-[0.08] relative rounded-[12.5rem] shrink-0 w-full"
					data-name="_Avatar contrast inner border"
				>
					<div
						aria-hidden="true"
						className="absolute border-[#101828] border-[0.046875rem] border-solid inset-0 pointer-events-none rounded-[12.5rem]"
					/>
				</div>
			</div>
			<div
				className="box-border content-stretch flex flex-col items-center justify-center mr-[-0.75rem] relative rounded-[12.5rem] shrink-0 size-[2.5rem]"
				data-name="Avatar"
			>
				<div
					aria-hidden="true"
					className="absolute inset-0 pointer-events-none rounded-[12.5rem]"
				>
					<div className="absolute bg-[#dadcd6] inset-0 rounded-[12.5rem]" />
					<Image
						alt=""
						className="absolute max-w-none object-[50%_50%] object-cover rounded-[12.5rem] size-full"
						src={img4}
						fill
					/>
				</div>
				<div
					aria-hidden="true"
					className="absolute border-[0.09375rem] border-solid border-white inset-[-0.09375rem] pointer-events-none rounded-[12.59375rem]"
				/>
				<div
					className="basis-0 grow min-h-px min-w-px opacity-[0.08] relative rounded-[12.5rem] shrink-0 w-full"
					data-name="_Avatar contrast inner border"
				>
					<div
						aria-hidden="true"
						className="absolute border-[#101828] border-[0.046875rem] border-solid inset-0 pointer-events-none rounded-[12.5rem]"
					/>
				</div>
			</div>
			<div
				className="box-border content-stretch flex flex-col items-center justify-center mr-[-0.75rem] relative rounded-[12.5rem] shrink-0 size-[2.5rem]"
				data-name="Avatar"
			>
				<div
					aria-hidden="true"
					className="absolute inset-0 pointer-events-none rounded-[12.5rem]"
				>
					<div className="absolute bg-[#d9d0e6] inset-0 rounded-[12.5rem]" />
					<Image
						alt=""
						className="absolute max-w-none object-[50%_50%] object-cover rounded-[12.5rem] size-full"
						src={img5}
						fill
					/>
				</div>
				<div
					aria-hidden="true"
					className="absolute border-[0.09375rem] border-solid border-white inset-[-0.09375rem] pointer-events-none rounded-[12.59375rem]"
				/>
				<div
					className="basis-0 grow min-h-px min-w-px opacity-[0.08] relative rounded-[12.5rem] shrink-0 w-full"
					data-name="_Avatar contrast inner border"
				>
					<div
						aria-hidden="true"
						className="absolute border-[#101828] border-[0.046875rem] border-solid inset-0 pointer-events-none rounded-[12.5rem]"
					/>
				</div>
			</div>
		</div>
	);
}

function Star() {
	return (
		<div className="absolute left-0 size-[1.25rem] top-0" data-name="Star">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 20 20"
			>
				<g clipPath="url(#clip0_0_10284)" id="Star">
					<path
						d={svgPaths.p2f878000}
						fill="var(--fill-0, #FEC84B)"
						id="Star_2"
					/>
				</g>
				<defs>
					<clipPath id="clip0_0_10284">
						<rect fill="white" height="20" width="20" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function Star1() {
	return (
		<div className="absolute left-0 size-[1.25rem] top-0" data-name="Star">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 20 20"
			>
				<g clipPath="url(#clip0_0_10284)" id="Star">
					<path
						d={svgPaths.p2f878000}
						fill="var(--fill-0, #FEC84B)"
						id="Star_2"
					/>
				</g>
				<defs>
					<clipPath id="clip0_0_10284">
						<rect fill="white" height="20" width="20" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function Star2() {
	return (
		<div className="absolute left-0 size-[1.25rem] top-0" data-name="Star">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 20 20"
			>
				<g clipPath="url(#clip0_0_10284)" id="Star">
					<path
						d={svgPaths.p2f878000}
						fill="var(--fill-0, #FEC84B)"
						id="Star_2"
					/>
				</g>
				<defs>
					<clipPath id="clip0_0_10284">
						<rect fill="white" height="20" width="20" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function Star3() {
	return (
		<div className="absolute left-0 size-[1.25rem] top-0" data-name="Star">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 20 20"
			>
				<g clipPath="url(#clip0_0_10284)" id="Star">
					<path
						d={svgPaths.p2f878000}
						fill="var(--fill-0, #FEC84B)"
						id="Star_2"
					/>
				</g>
				<defs>
					<clipPath id="clip0_0_10284">
						<rect fill="white" height="20" width="20" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function Star4() {
	return (
		<div className="absolute left-0 size-[1.25rem] top-0" data-name="Star">
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 20 20"
			>
				<g clipPath="url(#clip0_0_10284)" id="Star">
					<path
						d={svgPaths.p2f878000}
						fill="var(--fill-0, #FEC84B)"
						id="Star_2"
					/>
				</g>
				<defs>
					<clipPath id="clip0_0_10284">
						<rect fill="white" height="20" width="20" />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function Stars() {
	return (
		<div
			className="content-stretch flex gap-[0.25rem] items-center relative shrink-0"
			data-name="Stars"
		>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="Star icon"
			>
				<div
					className="absolute bottom-[-2.5%] left-0 right-0 top-[2.5%]"
					data-name="Star background"
				>
					<div className="absolute inset-[4.01%_6.13%_12.31%_6.13%]">
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 18 17"
						>
							<path
								d={svgPaths.p30303900}
								fill="var(--fill-0, #F2F4F7)"
								id="Star background"
							/>
						</svg>
					</div>
				</div>
				<Star />
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="Star icon"
			>
				<div
					className="absolute bottom-[-2.5%] left-0 right-0 top-[2.5%]"
					data-name="Star background"
				>
					<div className="absolute inset-[4.01%_6.13%_12.31%_6.13%]">
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 18 17"
						>
							<path
								d={svgPaths.p30303900}
								fill="var(--fill-0, #F2F4F7)"
								id="Star background"
							/>
						</svg>
					</div>
				</div>
				<Star1 />
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="Star icon"
			>
				<div
					className="absolute bottom-[-2.5%] left-0 right-0 top-[2.5%]"
					data-name="Star background"
				>
					<div className="absolute inset-[4.01%_6.13%_12.31%_6.13%]">
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 18 17"
						>
							<path
								d={svgPaths.p30303900}
								fill="var(--fill-0, #F2F4F7)"
								id="Star background"
							/>
						</svg>
					</div>
				</div>
				<Star2 />
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="Star icon"
			>
				<div
					className="absolute bottom-[-2.5%] left-0 right-0 top-[2.5%]"
					data-name="Star background"
				>
					<div className="absolute inset-[4.01%_6.13%_12.31%_6.13%]">
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 18 17"
						>
							<path
								d={svgPaths.p30303900}
								fill="var(--fill-0, #F2F4F7)"
								id="Star background"
							/>
						</svg>
					</div>
				</div>
				<Star3 />
			</div>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="Star icon"
			>
				<div
					className="absolute bottom-[-2.5%] left-0 right-0 top-[2.5%]"
					data-name="Star background"
				>
					<div className="absolute inset-[4.01%_6.13%_12.31%_6.13%]">
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 18 17"
						>
							<path
								d={svgPaths.p30303900}
								fill="var(--fill-0, #F2F4F7)"
								id="Star background"
							/>
						</svg>
					</div>
				</div>
				<Star4 />
			</div>
		</div>
	);
}

function Row1() {
	return (
		<div
			className="content-stretch flex gap-[0.5rem] items-center relative shrink-0"
			data-name="Row"
		>
			<Stars />
			<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[1.5rem] not-italic relative shrink-0 text-[1rem] text-nowrap text-white whitespace-pre">
				5.0
			</p>
		</div>
	);
}

function Reviews() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.25rem] items-start relative shrink-0"
			data-name="Reviews"
		>
			<Row1 />
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-[1.5rem] not-italic relative shrink-0 text-[#8c99a7] text-[1rem] text-nowrap whitespace-pre">
				from 50+ investors
			</p>
		</div>
	);
}

function PromoSection2({
	onUpgrade,
	isUpgrading,
}: {
	onUpgrade?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<motion.div
			id="payment"
			className="relative bg-[#1a2c20] overflow-hidden w-full max-w-[90rem] mx-auto flex flex-col gap-10 md:gap-12 px-[9.375rem] py-14 md:py-16 lg:py-20"
			data-name="Promo Section"
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<div className="relative flex flex-row flex-wrap gap-10 lg:gap-14 items-start justify-between w-full">
				<InfoSectionContainer />
				<PaymentSectionCard
					onClick={onUpgrade}
					isUpgrading={isUpgrading}
				/>
			</div>
		</motion.div>
	);
}

function PropertyViewSearchIcon() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="search-lg"
			>
				<div className="absolute inset-[12.5%]" data-name="Icon">
					<div
						className="absolute inset-[-5.56%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 17 17"
						>
							<path
								d={svgPaths.p3190da80}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.66667"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic relative shrink-0 text-[0.9375rem] sm:text-[1rem] text-white">
				Search by address, county and more
			</p>
		</div>
	);
}

function PropertyViewFilterIcon() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="filter-funnel-01"
			>
				<div
					className="absolute inset-[12.5%_8.33%_13.51%_8.33%]"
					data-name="Icon"
				>
					<div
						className="absolute inset-[-5.63%_-5%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 19 17"
						>
							<path
								d={svgPaths.p2124d180}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.66667"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic relative shrink-0 text-[0.9375rem] sm:text-[1rem] text-white">
				Quick Filters
			</p>
		</div>
	);
}

function PropertyViewOwnerIcon() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<div
				className="overflow-clip relative shrink-0 size-[1.25rem]"
				data-name="user-check-02"
			>
				<div
					className="absolute inset-[16.67%_8.33%_12.5%_8.33%]"
					data-name="Icon"
				>
					<div
						className="absolute inset-[-5.88%_-5%]"
						style={
							{
								"--stroke-0": "rgba(167, 255, 94, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 19 16"
						>
							<path
								d={svgPaths.p35f5ad00}
								id="Icon"
								stroke="var(--stroke-0, #A7FF5E)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="1.66667"
							/>
						</svg>
					</div>
				</div>
			</div>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic relative shrink-0 text-[0.9375rem] sm:text-[1rem] text-white">
				500+ owner fields live in the grid
			</p>
		</div>
	);
}

function PropertyViewTitle() {
	return (
		<div className="content-stretch flex flex-col gap-2 items-start w-full max-w-[32rem] shrink-0">
			<div className="flex items-center gap-2.5 w-full">
				<span
					aria-hidden
					className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#A7FF5E]/12 text-[0.72rem] font-bold tabular-nums text-[#A7FF5E] ring-1 ring-[#A7FF5E]/25"
				>
					01
				</span>
				<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-none text-[0.6875rem] sm:text-[0.7rem] uppercase tracking-[0.14em] text-[#A7FF5E] m-0">
					Property view
				</p>
			</div>
			<p className="font-['Inter:Bold',sans-serif] font-bold leading-[1.3] sm:leading-[1.34] not-italic text-[#edffde] text-[1.35rem] sm:text-[1.5rem] md:text-[1.65rem] lg:text-[1.55rem] xl:text-[1.4rem] w-full m-0">
				Find any asset in seconds—then export or drill into details
			</p>
		</div>
	);
}

function PropertyViewCard() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter w-full h-full flex-1 flex flex-col min-h-[32rem] md:min-h-[34rem] lg:min-h-[36rem] xl:min-h-[30rem] rounded-[1.5rem] md:rounded-[2rem] overflow-hidden ring-1 ring-[#A7FF5E]/14 shadow-[0_28px_72px_-28px_rgba(0,0,0,0.75)] group"
			style={{ background: WORKSPACE_CARD_SURFACE }}
			initial={{ opacity: 0, y: 28 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
			whileHover={{ y: -4, transition: { duration: 0.3 } }}
		>
			<div className="flex-1 min-h-0 h-full overflow-clip relative rounded-[inherit] w-full flex flex-col p-5 md:p-6 xl:p-5 gap-3 md:gap-4">
				<PropertyViewTitle />
				<div className="grid grid-cols-1 gap-2 shrink-0 w-full sm:grid-cols-2 sm:gap-2 xl:grid-cols-1">
					<PropertyViewSearchIcon />
					<PropertyViewFilterIcon />
					<div className="min-w-0 sm:col-span-2 xl:col-span-1">
						<PropertyViewOwnerIcon />
					</div>
				</div>
				<div
					className="relative flex-1 min-h-[11rem] sm:min-h-[12rem] w-full rounded-[1.25rem] md:rounded-[1.75rem] overflow-hidden pointer-events-none mt-auto border border-white/[0.06]"
					data-name="image 33"
				>
					<Image
						alt=""
						className="object-cover object-[50%_45%] rounded-[inherit]"
						src={imgImage33}
						fill
						sizes="(max-width: 1280px) 100vw, 34vw"
					/>
					<div
						aria-hidden="true"
						className="absolute border-[0.375rem] md:border-8 border-[rgba(255,255,255,0.2)] border-solid inset-[-0.25rem] md:inset-[-0.5rem] rounded-[2rem] md:rounded-[2.25rem] pointer-events-none"
					/>
				</div>
			</div>
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[inherit] opacity-0 group-hover:opacity-[0.22] transition-opacity duration-300"
				style={{
					boxShadow:
						"0 0 24px rgba(167, 255, 94, 0.2), inset 0 0 20px rgba(167, 255, 94, 0.05)",
				}}
			/>
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.2)] border-solid inset-0 pointer-events-none rounded-[inherit]"
			/>
		</motion.div>
	);
}

function MapViewTitle() {
	return (
		<div className="content-stretch flex flex-col gap-2 items-start w-full max-w-[32rem] shrink-0">
			<div className="flex items-center gap-2.5 w-full">
				<span
					aria-hidden
					className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#A7FF5E]/12 text-[0.72rem] font-bold tabular-nums text-[#A7FF5E] ring-1 ring-[#A7FF5E]/25"
				>
					02
				</span>
				<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-none text-[0.6875rem] sm:text-[0.7rem] uppercase tracking-[0.14em] text-[#A7FF5E] m-0">
					Map view
				</p>
			</div>
			<p className="font-['Inter:Bold',sans-serif] font-bold leading-[1.3] sm:leading-[1.34] not-italic text-[#edffde] text-[1.35rem] sm:text-[1.5rem] md:text-[1.65rem] lg:text-[1.55rem] xl:text-[1.4rem] w-full m-0">
				Spot deals geographically—prices on the map, not a spreadsheet
			</p>
		</div>
	);
}

function MapViewFeaturePins() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<MapPin
				className="size-[1.25rem] shrink-0 text-[#A7FF5E]"
				strokeWidth={1.85}
				aria-hidden
			/>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic text-[0.9375rem] sm:text-[1rem] text-white m-0">
				Asking price on every pin—pan and zoom freely
			</p>
		</div>
	);
}

function MapViewFeatureAreas() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<MapLucideIcon
				className="size-[1.25rem] shrink-0 text-[#A7FF5E]"
				strokeWidth={1.85}
				aria-hidden
			/>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic text-[0.9375rem] sm:text-[1rem] text-white m-0">
				Neighborhoods &amp; corridors that match your buy box
			</p>
		</div>
	);
}

function MapViewFeatureSync() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<Link2
				className="size-[1.25rem] shrink-0 text-[#A7FF5E]"
				strokeWidth={1.85}
				aria-hidden
			/>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic text-[0.9375rem] sm:text-[1rem] text-white m-0">
				Same filters as your grid—the map updates instantly
			</p>
		</div>
	);
}

function MapViewCard() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter w-full h-full flex-1 flex flex-col min-h-[32rem] md:min-h-[34rem] lg:min-h-[36rem] xl:min-h-[30rem] rounded-[1.5rem] md:rounded-[2rem] overflow-hidden ring-1 ring-[#A7FF5E]/14 shadow-[0_24px_64px_-28px_rgba(0,0,0,0.7)] group"
			style={{ background: WORKSPACE_CARD_SURFACE }}
			initial={{ opacity: 0, y: 28 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{
				duration: 0.55,
				delay: 0.06,
				ease: [0.22, 1, 0.36, 1],
			}}
			whileHover={{ y: -4, transition: { duration: 0.3 } }}
		>
			<div className="flex-1 min-h-0 h-full overflow-clip relative rounded-[inherit] w-full flex flex-col p-5 md:p-6 xl:p-5 gap-3 md:gap-4">
				<MapViewTitle />
				<div className="grid grid-cols-1 gap-2 shrink-0 w-full sm:grid-cols-2 sm:gap-2 xl:grid-cols-1">
					<MapViewFeaturePins />
					<MapViewFeatureAreas />
					<div className="min-w-0 sm:col-span-2 xl:col-span-1">
						<MapViewFeatureSync />
					</div>
				</div>
				<div
					className="relative flex-1 min-h-[11rem] sm:min-h-[12rem] w-full rounded-[1.25rem] md:rounded-[1.75rem] overflow-hidden pointer-events-none mt-auto border border-white/[0.06]"
					data-name="image 34"
				>
					<Image
						alt=""
						className="object-cover object-[center_35%] rounded-[inherit]"
						src={imgImage34}
						fill
						sizes="(max-width: 1280px) 100vw, 34vw"
						priority={false}
					/>
				</div>
			</div>
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[inherit] opacity-0 group-hover:opacity-[0.2] transition-opacity duration-300"
				style={{
					boxShadow: "0 0 20px rgba(167, 255, 94, 0.18)",
				}}
			/>
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.2)] border-solid inset-0 pointer-events-none rounded-[inherit]"
			/>
		</motion.div>
	);
}

function AuctionViewTitle() {
	return (
		<div className="content-stretch flex flex-col gap-2 items-start w-full max-w-[32rem] shrink-0">
			<div className="flex items-center gap-2.5 w-full">
				<span
					aria-hidden
					className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#A7FF5E]/12 text-[0.72rem] font-bold tabular-nums text-[#A7FF5E] ring-1 ring-[#A7FF5E]/25"
				>
					03
				</span>
				<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-none text-[0.6875rem] sm:text-[0.7rem] uppercase tracking-[0.14em] text-[#A7FF5E] m-0">
					Auction view
				</p>
			</div>
			<p className="font-['Inter:Bold',sans-serif] font-bold leading-[1.3] sm:leading-[1.34] not-italic text-[#edffde] text-[1.35rem] sm:text-[1.5rem] md:text-[1.65rem] lg:text-[1.55rem] xl:text-[1.4rem] w-full m-0">
				Never miss a sale date
			</p>
		</div>
	);
}

function AuctionViewFeatureLanes() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<CalendarDays
				className="size-[1.25rem] shrink-0 text-[#A7FF5E]"
				strokeWidth={1.85}
				aria-hidden
			/>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic text-[0.9375rem] sm:text-[1rem] text-white m-0">
				Auction lanes for your current filters—week by week
			</p>
		</div>
	);
}

function AuctionViewFeatureTable() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<Columns3
				className="size-[1.25rem] shrink-0 text-[#A7FF5E]"
				strokeWidth={1.85}
				aria-hidden
			/>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic text-[0.9375rem] sm:text-[1rem] text-white m-0">
				Plan capital &amp; site visits without tab-hopping
			</p>
		</div>
	);
}

function AuctionViewFeatureExport() {
	return (
		<div className={WORKSPACE_FEATURE_ROW_CLASS}>
			<Download
				className="size-[1.25rem] shrink-0 text-[#A7FF5E]"
				strokeWidth={1.85}
				aria-hidden
			/>
			<p className="font-['Inter:Medium',sans-serif] font-medium leading-snug not-italic text-[0.9375rem] sm:text-[1rem] text-white m-0">
				Export sale windows when you&apos;re ready to move
			</p>
		</div>
	);
}

function AuctionViewCard() {
	return (
		<motion.div
			className="relative backdrop-blur-md backdrop-filter w-full h-full flex-1 flex flex-col min-h-[32rem] md:min-h-[34rem] lg:min-h-[36rem] xl:min-h-[30rem] rounded-[1.5rem] md:rounded-[2rem] overflow-hidden ring-1 ring-[#A7FF5E]/14 shadow-[0_24px_64px_-28px_rgba(0,0,0,0.7)] group"
			style={{ background: WORKSPACE_CARD_SURFACE }}
			initial={{ opacity: 0, y: 28 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{
				duration: 0.55,
				delay: 0.12,
				ease: [0.22, 1, 0.36, 1],
			}}
			whileHover={{ y: -4, transition: { duration: 0.3 } }}
		>
			<div className="flex-1 min-h-0 h-full overflow-clip relative rounded-[inherit] w-full flex flex-col p-5 md:p-6 xl:p-5 gap-3 md:gap-4">
				<AuctionViewTitle />
				<div className="grid grid-cols-1 gap-2 shrink-0 w-full sm:grid-cols-2 sm:gap-2 xl:grid-cols-1">
					<AuctionViewFeatureLanes />
					<AuctionViewFeatureTable />
					<div className="min-w-0 sm:col-span-2 xl:col-span-1">
						<AuctionViewFeatureExport />
					</div>
				</div>
				<div
					className="relative flex-1 min-h-[11rem] sm:min-h-[12rem] w-full rounded-[1.25rem] md:rounded-[1.75rem] overflow-hidden pointer-events-none mt-auto border border-white/[0.06]"
					data-name="image 35"
				>
					<Image
						alt=""
						className="object-cover object-top rounded-[inherit]"
						src={imgImage35}
						fill
						sizes="(max-width: 1280px) 100vw, 34vw"
					/>
					<div
						aria-hidden="true"
						className="absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-white/[0.08]"
					/>
				</div>
			</div>
			<div
				aria-hidden="true"
				className="absolute border-2 border-[#A7FF5E] border-solid inset-0 pointer-events-none rounded-[inherit] opacity-0 group-hover:opacity-[0.2] transition-opacity duration-300"
				style={{
					boxShadow: "0 0 20px rgba(167, 255, 94, 0.18)",
				}}
			/>
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.2)] border-solid inset-0 pointer-events-none rounded-[inherit]"
			/>
		</motion.div>
	);
}

function InfoIconContainer2() {
	return (
		<div
			className="relative shrink-0 size-[4rem]"
			data-name="Info Icon Container"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 64 64"
			>
				<foreignObject height="112" width="112" x="-24" y="-24">
					<div
						style={{
							backdropFilter: "blur(12px)",
							clipPath: "url(#bgblur_0_0_10200_clip_path)",
							height: "100%",
							width: "100%",
						}}
					/>
				</foreignObject>
				<g data-figma-bg-blur-radius="24" id="Info Icon Container">
					<path
						d={svgPaths.p30f5690}
						fill="var(--fill-0, black)"
						fillOpacity="0.05"
					/>
					<path
						d={svgPaths.p30f5690}
						fill="var(--fill-1, #65DE9E)"
						fillOpacity="0.12"
					/>
					<path
						d={svgPaths.p35809340}
						fill="var(--fill-0, white)"
						id="Icon"
					/>
				</g>
				<defs>
					<clipPath
						id="bgblur_0_0_10200_clip_path"
						transform="translate(24 24)"
					>
						<path d={svgPaths.p30f5690} />
					</clipPath>
				</defs>
			</svg>
		</div>
	);
}

function InfoHeader() {
	return (
		<div
			className="content-stretch flex flex-col gap-[1.25rem] items-start relative shrink-0"
			data-name="Info Header"
		>
			<InfoIconContainer2 />
			<p
				className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[2.5rem] relative shrink-0 text-[2rem] text-white tracking-[0.03125rem] w-[26.25rem]"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Professional-Grade Property Intelligence
			</p>
		</div>
	);
}

function InfoDescriptionContainer() {
	return (
		<div
			className="content-stretch flex flex-col gap-[0.5rem] items-start leading-[1.5rem] not-italic relative shrink-0 text-[0.875rem] w-full"
			data-name="Info Description Container"
		>
			<p className="font-['Inter:Bold',sans-serif] font-bold relative shrink-0 text-white w-full">
				Outperform with Tax Lien Vest: Smarter, Faster Tax Lien
				Investing
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal relative shrink-0 text-[rgba(255,255,255,0.7)] tracking-[0.03125rem] w-full">
				Professional investors need accurate, comprehensive data to make
				confident investment decisions. Tax Lien Vest eliminates
				research guesswork by providing institutional-grade property
				intelligence that transforms complex tax lien analysis into
				streamlined investment workflows.
			</p>
		</div>
	);
}

function InfoContainer2() {
	return (
		<div
			className="relative content-stretch flex flex-col gap-[1.25rem] items-start w-full"
			data-name="Info Container"
			style={{ paddingLeft: "4.5rem" }}
		>
			<InfoHeader />
			<InfoDescriptionContainer />
		</div>
	);
}

function Group1() {
	return (
		<div
			className="absolute bottom-[0.4%] left-0 right-0 top-0"
			data-name="Group"
		>
			<svg
				className="block size-full"
				fill="none"
				preserveAspectRatio="none"
				viewBox="0 0 120 84"
			>
				<g id="Group">
					<path
						d={svgPaths.p11324ef0}
						fill="var(--fill-0, #66B327)"
						id="Vector"
					/>
					<path
						d={svgPaths.pe25380}
						fill="var(--fill-0, white)"
						id="Vector_2"
					/>
				</g>
			</svg>
		</div>
	);
}

function YoutubeIconLogo() {
	return (
		<div
			className="absolute h-[5.25rem] left-[calc(50%-16px)] overflow-clip top-1/2 translate-x-[-50%] translate-y-[-50%] w-[7.5rem]"
			data-name="youtube-icon logo"
		>
			<Group1 />
		</div>
	);
}

function ImageContainer() {
	return (
		<div
			className="relative backdrop-blur-md backdrop-filter h-[30.75rem] overflow-clip flex-1 min-w-[36.375rem]"
			data-name="Image Container"
		>
			<div
				className="absolute h-[25.4375rem] left-[1.4375rem] pointer-events-none rounded-[1.5rem] top-[2.625rem] w-[31.4375rem]"
				data-name="Main Image"
			>
				<div
					aria-hidden="true"
					className="absolute inset-0 rounded-[1.5rem]"
				>
					<div className="absolute inset-0 overflow-hidden rounded-[1.5rem]">
						<Image
							alt=""
							className="absolute h-[105.69%] left-0 max-w-none top-0 w-[99.93%]"
							src={imgMainImage}
							fill
							sizes="31.4375rem"
						/>
					</div>
					<div className="absolute bg-[rgba(0,0,0,0.67)] inset-0 rounded-[1.5rem]" />
				</div>
				<div
					aria-hidden="true"
					className="absolute border-4 border-[rgba(255,255,255,0.1)] border-solid inset-[-0.25rem] rounded-[1.75rem]"
				/>
			</div>
			<YoutubeIconLogo />
		</div>
	);
}

function RightArrowContainer() {
	return (
		<div
			className="absolute backdrop-blur-md backdrop-filter left-[75.5rem] overflow-clip rounded-[5.3125rem] size-[4.5rem] top-[13.3125rem]"
			data-name="Right Arrow Container"
			style={{
				backgroundImage:
					"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)",
			}}
		>
			<div className="absolute flex items-center justify-center left-1/2 size-[2.5rem] top-[1rem] translate-x-[-50%]">
				<div className="flex-none rotate-[180deg] scale-y-[-100%]">
					<div
						className="overflow-clip relative size-[2.5rem]"
						data-name="Right Arrow"
					>
						<div
							className="absolute bottom-1/4 left-[37.5%] right-[37.5%] top-1/4"
							data-name="Icon"
						>
							<div
								className="absolute inset-[-10%_-20%]"
								style={
									{
										"--stroke-0": "rgba(167, 255, 94, 1)",
									} as React.CSSProperties
								}
							>
								<svg
									className="block size-full"
									fill="none"
									preserveAspectRatio="none"
									viewBox="0 0 14 24"
								>
									<path
										d="M12 22L2 12L12 2"
										id="Icon"
										stroke="var(--stroke-0, #A7FF5E)"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="4"
									/>
								</svg>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function LeftArrowContainer() {
	return (
		<div
			className="absolute backdrop-blur-md backdrop-filter left-0 rounded-[5.3125rem] size-[4.5rem] top-[13.125rem]"
			data-name="Left Arrow Container"
			style={{
				backgroundImage:
					"linear-gradient(90deg, rgba(101, 222, 158, 0.12) 0%, rgba(101, 222, 158, 0.12) 100%), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.05) 100%)",
			}}
		>
			<div className="flex flex-col items-center justify-center overflow-clip rounded-[inherit] size-full">
				<div className="box-border content-stretch flex flex-col gap-[2.25rem] items-center justify-center px-[3rem] py-[2rem] relative size-[4.5rem]">
					<div
						className="overflow-clip relative shrink-0 size-[2.5rem]"
						data-name="Left Arrow"
					>
						<div
							className="absolute bottom-1/4 left-[37.5%] right-[37.5%] top-1/4"
							data-name="Icon"
						>
							<div
								className="absolute inset-[-10%_-20%]"
								style={
									{
										"--stroke-0": "rgba(167, 255, 94, 1)",
									} as React.CSSProperties
								}
							>
								<svg
									className="block size-full"
									fill="none"
									preserveAspectRatio="none"
									viewBox="0 0 14 24"
								>
									<path
										d="M12 22L2 12L12 2"
										id="Icon"
										stroke="var(--stroke-0, #A7FF5E)"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="4"
									/>
								</svg>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function Dot() {
	return (
		<div
			className="bg-[#a7ff5e] h-[0.5rem] rounded-[3.5rem] shrink-0 w-[1.25rem]"
			data-name="Dot"
		/>
	);
}

function Dot1() {
	return (
		<div
			className="bg-[rgba(255,255,255,0.1)] rounded-[3.5rem] shrink-0 size-[0.5rem]"
			data-name="Dot"
		/>
	);
}

function PaginationDots() {
	return (
		<div
			className="absolute content-stretch flex gap-[0.5rem] items-center left-[1.5rem] top-[0.75rem]"
			data-name="Pagination Dots"
		>
			<Dot />
			{[...Array(5).keys()].map((_, i) => (
				<Dot1 key={i} />
			))}
		</div>
	);
}

function CarouselPagination() {
	return (
		<div className="relative backdrop-blur-md backdrop-filter bg-[#22402e] h-[2rem] rounded-[1.25rem] w-[9.25rem] mx-auto">
			<div
				aria-hidden="true"
				className="absolute border border-[rgba(255,255,255,0.12)] border-solid inset-0 pointer-events-none rounded-[1.25rem]"
			/>
			<PaginationDots />
		</div>
	);
}

function TryFreeButtonHowItWorks({
	onClick,
	isUpgrading,
}: {
	onClick?: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<motion.button
			className="relative bg-[#a7ff5e] h-[4rem] rounded-[4.25rem] w-[55%] max-w-[37.5rem] min-w-[18.75rem] cursor-pointer mx-auto disabled:opacity-70 disabled:cursor-not-allowed"
			data-name="Try Free Button How It Works"
			data-testid="try-free-transform-button"
			type="button"
			style={{ cursor: isUpgrading ? "not-allowed" : "pointer" }}
			initial={{ opacity: 0, scale: 0.8, y: 20 }}
			animate={{ opacity: 1, scale: 1, y: 0 }}
			whileInView={{ opacity: 1, scale: 1, y: 0 }}
			viewport={{ once: true, amount: 0.1 }}
			transition={{
				type: "spring",
				stiffness: 400,
				damping: 17,
				duration: 0.6,
			}}
			whileHover={isUpgrading ? {} : { scale: 1.02 }}
			whileTap={isUpgrading ? {} : { scale: 0.98 }}
			onClick={onClick}
			disabled={isUpgrading}
		>
			<div className="box-border content-stretch flex gap-[0.25rem] h-[4rem] items-center justify-center overflow-clip px-[2rem] py-[0.5rem] relative rounded-[inherit] w-full">
				{isUpgrading ? (
					<div className="flex items-center gap-2">
						<Loader2 className="w-5 h-5 text-[#12301b] animate-spin" />
						<p className="font-['Inter:Extra_Bold',sans-serif] font-extrabold not-italic text-[#12301b] text-[1rem] text-nowrap tracking-[0.01rem] whitespace-pre">
							Redirecting...
						</p>
					</div>
				) : (
					<PromoTextContainer />
				)}
			</div>
			<div
				aria-hidden="true"
				className="absolute border border-[#dde3e9] border-solid inset-0 pointer-events-none rounded-[4.25rem] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)]"
			/>
		</motion.button>
	);
}

function CenterContent() {
	return (
		<div className="relative flex flex-col flex-1 min-w-[31.25rem]">
			<InfoContainer2 />
		</div>
	);
}

function CarouselSection() {
	return (
		<div className="relative flex flex-col gap-6 w-full px-[5.9375rem] py-4 md:py-6">
			<div className="relative flex flex-row items-center">
				<LeftArrowContainer />
				<div
					className="relative bg-[#1a2c20] h-[30.75rem] overflow-clip rounded-[2.75rem] flex flex-row flex-1 gap-8"
					style={{ alignItems: "center" }}
				>
					<CenterContent />
					<ImageContainer />
				</div>
				<RightArrowContainer />
			</div>
			<CarouselPagination />
		</div>
	);
}

export default function LandingPageContent() {
	const { width } = useWindowSize();
	const isMobile = width > 0 && width < 768;
	const [isUpgrading, setIsUpgrading] = useState(false);
	const [enrichmentEnabled, setEnrichmentEnabled] = useState(false);

	useEffect(() => {
		getConfig()
			.then((c) => setEnrichmentEnabled(!!c.enrichment))
			.catch(() => {});
	}, []);

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	// Render mobile view for screens < 768px
	if (isMobile) {
		return <MobileLandingPageContent />;
	}

	// Desktop view
	return (
		<div
			className="bg-[#16251c] relative w-full min-h-[370.5rem] flex flex-col gap-24"
			data-name="Landing Page"
		>
			<ScalableContainer baseWidth={1440}>
				<div className="relative w-full flex flex-col gap-24">
					<HeroSection
						onUpgrade={handleUpgrade}
						isUpgrading={isUpgrading}
						enrichmentEnabled={enrichmentEnabled}
					/>
					<USCoverageMapLazy />
					<FeatureSectionsContainer
						onUpgrade={handleUpgrade}
						isUpgrading={isUpgrading}
					/>
					<HowItWorksSection />
					<FeatureAndPropertyContainer
						onUpgrade={handleUpgrade}
						isUpgrading={isUpgrading}
					/>
					<PromoSection2
						onUpgrade={handleUpgrade}
						isUpgrading={isUpgrading}
					/>
					<PromoSection1 />
					<CarouselSection />
					<PromoSection onUpgrade={handleUpgrade} />
				</div>
			</ScalableContainer>
		</div>
	);
}
